import type { ComponentType } from "react";

export type FAQItem = { q: string; a: string };

export type TocItem = { id: string; label: string };

export type ArticleVisibility = "public" | "academy" | "preview";

export type Article = {
  slug: string;
  title: string;
  /** Optional shorter title for search results (kept under ~48 chars). */
  seoTitle?: string;
  excerpt: string;
  category: string;
  author: string;
  publishedAt: string; // ISO
  updatedAt: string; // ISO
  readTime: string;
  coverGradient: string; // tailwind classes for hero
  metaDescription: string;
  focusKeyword: string;
  secondaryKeywords: string[];
  keyTakeaways: string[];
  toc: TocItem[];
  faq: FAQItem[];
  relatedSlugs: string[];
  Content: () => Promise<{ default: ComponentType }>;
  /**
   * Visibility contract — controls public vs paid surfaces.
   *  - "public":  /education feed + full /education/$slug
   *  - "preview": /education feed shows SEO teaser; full lesson lives in Academy
   *  - "academy": NOT shown publicly at all (404 on /education/$slug)
   * Defaults to "public" if omitted (legacy entries).
   */
  visibility?: ArticleVisibility;
  /** Optional shorter summary used on the public preview page. */
  previewExcerpt?: string;
  /** Optional 2–3 takeaways shown on the public preview page. */
  previewTakeaways?: string[];
  /** Optional hero image URL. When set, populates og:image, twitter:image, and BlogPosting.image. */
  heroImage?: string;
  /** Optional embedded YouTube video, emitted as VideoObject JSON-LD. */
  video?: { youtubeId: string; name: string; description: string; uploadDate: string; duration: string };
};

import { fairValueGapDiagramImage } from "@/content/articles/fair-value-gap-xauusd-guide";
import breakerBlockCover from "@/assets/articles/breaker-block-gold-diagram.jpg.asset.json";
import premiumDiscountCover from "@/assets/articles/premium-discount-gold-diagram.jpg.asset.json";
import goldSessionsCover from "@/assets/articles/gold-sessions-cycle-diagram.jpg.asset.json";
import sentimentVsSmcCover from "@/assets/articles/sentiment-vs-smc-diagram.jpg.asset.json";
import inducementTrapCover from "@/assets/articles/inducement-trap-gold-diagram.jpg.asset.json";
import goldRetailSentimentCover from "@/assets/articles/gold-retail-sentiment-diagram.jpg.asset.json";
import fvgVoidCover from "@/assets/articles/fvg-vs-liquidity-void-cover.svg.asset.json";
import goldSlippageCover from "@/assets/articles/gold-slippage-liquidity-blackout.jpg.asset.json";
import { goldCorrection5600HeroImage } from "@/content/articles/gold-correction-5600-peak-macro-analysis";

export const articles: Article[] = [
  {
    slug: "gold-swing-trading-strategy-case-study",
    title: "Gold Swing Trading Strategy: Anatomy of a 210 USD XAUUSD Swing Trade (Audited Replay)",
    seoTitle: "Gold Swing Trade Case Study: 210 USD XAUUSD Move",
    excerpt:
      "An audited XAUUSD swing short from 4,141.78 to 3,931.22: options walls, MacroLens context, an independent review and a candle by candle replay of all 11 days.",
    category: "Case Study",
    author: "XAU Pro Academy Team",
    publishedAt: "2026-10-22",
    updatedAt: "2026-10-30",
    readTime: "10 min",
    coverGradient: "from-gold-muted via-card to-slate-900",
    heroImage: "/images/articles/gold-swing-trade-210-usd-cover.svg",
    metaDescription:
      "Gold swing trading strategy case study: an audited XAUUSD H4 short captured 210 USD (+2.94R) over 11 days. Options walls, MacroLens and an interactive replay.",
    focusKeyword: "gold swing trading strategy",
    secondaryKeywords: ["gold swing trade", "xauusd swing trade", "swing trading gold", "gold options call wall", "gld call wall", "gold swing trade case study", "xauusd trade replay", "breakeven runner"],
    keyTakeaways: [
      "An H4 short from 4,141.78 reached its final target at 3,931.22, a 210.56 USD move and +2.94R net.",
      "The entry sat beneath the 4,140 options Call Wall, where the pre entry range compressed without a sustained break higher.",
      "Half closed at Target 1 on 22 October and the stop moved to entry, so the last eight days carried zero capital risk.",
      "An independent reviewer judged the candles alone as modest confluence. Options and macro context explain why the setup held.",
      "Every level and candle is verifiable in the public ledger and the embedded replay.",
    ],
    toc: [
      { id: "replay", label: "Replay the Trade" },
      { id: "why-swing", label: "Why Swing Trading Suits Gold" },
      { id: "context", label: "Market Context" },
      { id: "options", label: "Options Walls: The 4,140 Ceiling" },
      { id: "macrolens", label: "MacroLens Context" },
      { id: "independent-review", label: "Independent Review" },
      { id: "management", label: "Managing for 11 Days" },
      { id: "checklist", label: "Gold Swing Trading Checklist" },
      { id: "conclusion", label: "Conclusion" },
      { id: "faq", label: "Frequently Asked Questions" },
    ],
    faq: [
      { q: "What is the best timeframe for swing trading gold?", a: "The 4 hour chart is the most practical for XAUUSD swing trades, with the Daily chart for context. It filters intraday noise while still producing several setups per month." },
      { q: "How many pips is a 210 dollar move on gold?", a: "On XAUUSD a pip is usually 0.10 dollars, so a 210 dollar move equals about 2,100 pips." },
      { q: "What is a Call Wall in gold options?", a: "The strike with the highest call open interest. Dealer hedging near that strike tends to absorb buying, so price often stalls beneath it. It is context, not a standalone signal." },
      { q: "How long are gold swing trades held?", a: "From one day to two weeks. This case was held 10 days and 21 hours, with zero capital risk after Target 1 on day three." },
      { q: "Why close only half of the position at Target 1?", a: "Securing half locks in profit, and moving the stop to entry removes the remaining risk, while the runner keeps exposure to the full move." },
      { q: "Where should the stop loss go on a gold swing trade?", a: "Beyond the structural zone that defines the setup. Very tight stops on gold are often hit by normal session volatility." },
    ],
    relatedSlugs: ["gold-traders-blind-spot-macrolens", "gold-retail-sentiment-contrarian-liquidity", "liquidity-sweep-gold-trading"],
    Content: () => import("@/content/articles/gold-swing-trading-strategy-case-study").then((m) => ({ default: m.GoldSwingCaseStudyContent })),
  },
  {
    slug: "gold-traders-blind-spot-macrolens",
    title: "The Gold Trader's Blind Spot: Why Technical Analysis Alone Fails on XAUUSD",
    seoTitle: "Gold Trader's Blind Spot: Macro vs Technicals",
    excerpt:
      "Why textbook SMC setups on gold fail when macro capital disagrees, and how MacroLens combines real yields, COT, CPI, NFP, options and structure into one score.",
    category: "Macro Research",
    author: "XAU Pro Academy Team",
    publishedAt: "2026-10-22",
    updatedAt: "2026-10-22",
    readTime: "9 min",
    coverGradient: "from-gold-muted via-card to-slate-900",
    heroImage: "/images/articles/gold-traders-blind-spot-macrolens-cover.svg",
    metaDescription:
      "Why technical analysis alone fails on XAUUSD. Learn how real yields, COT, CPI, NFP and options drive gold, and how the MacroLens score turns them into one reading.",
    focusKeyword: "gold macro analysis",
    secondaryKeywords: ["why smc fails on gold", "order blocks keep failing", "losing with technical analysis", "XAUUSD fundamentals", "real yields and gold", "gold COT positioning", "macro confluence trading", "MacroLens"],
    keyTakeaways: [
      "A correct chart reading can still fail when macro capital moves the other way.",
      "US real yields are the opportunity cost of holding gold and a primary driver of its direction.",
      "COT positioning, CPI and NFP surprises, options and sentiment add institutional context.",
      "MacroLens scores confluence from -100 to +100 across seven classifications, and a neutral reading is valid.",
      "Macro qualifies a setup. Entry and invalidation still come from structure and liquidity.",
    ],
    toc: [
      { id: "introduction", label: "The Illusion of the Flawless Pattern" },
      { id: "gold-is-different", label: "Why Gold Is Different" },
      { id: "real-yields", label: "Real Yields and Gold" },
      { id: "pillars", label: "Institutional Confluence Pillars" },
      { id: "macrolens-scale", label: "The MacroLens Scale" },
      { id: "operational-rules", label: "Execution Permission Rules" },
      { id: "why-smc-fails", label: "Why SMC Fails on Gold" },
      { id: "conclusion", label: "Conclusion" },
      { id: "faq", label: "Frequently Asked Questions" },
    ],
    faq: [
      { q: "Why do technical setups on gold fail?", a: "Gold is a global monetary asset. Large allocators reposition on real yields, inflation and labour data, and that flow can override a valid intraday pattern." },
      { q: "Why do my order blocks keep failing on XAUUSD?", a: "Common causes are POIs without a liquidity sweep or displacement, and zones already mitigated. On gold, valid zones also fail when the weekly macro flow opposes the trade direction." },
      { q: "Does SMC still work on gold?", a: "Structure and liquidity remain the basis for entries and invalidation. Results improve when setups are filtered by macro context instead of taken in isolation." },
      { q: "How do real yields affect gold?", a: "Rising real yields raise the cost of holding a non yielding asset and tend to pressure gold. Falling real yields tend to support it." },
      { q: "What is the MacroLens score range?", a: "The score runs from -100 to +100. +60 and above is Strong Bullish Confluence, +40 to +59 Bullish, +20 to +39 Moderately Bullish, -19 to +19 Neutral / Balanced, -39 to -20 Moderately Bearish, -59 to -40 Bearish, and -60 and below Strong Bearish Confluence." },
      { q: "Can MacroLens replace technical analysis?", a: "No. It qualifies direction and conviction. Entries, stops and targets still come from market structure, liquidity and a valid point of interest." },
    ],
    relatedSlugs: ["gold-correction-5600-peak-macro-analysis", "us-cpi-september-2026-gold-reaction", "high-probability-entry-criteria-gold"],
    Content: () => import("@/content/articles/gold-traders-blind-spot-macrolens").then((m) => ({ default: m.GoldTradersBlindSpotContent })),
  },
  {
    slug: "us-cpi-september-2026-gold-reaction",
    title: "US CPI September 2026: Hot Core Inflation and What It Means for Gold",
    seoTitle: "US CPI Sept 2026: Core 0.3% Hot, Gold Impact",
    excerpt:
      "September CPI beat forecasts: headline 3.7% y/y vs 3.6%, core 0.3% m/m vs 0.2%. Why core matters most, how XAUUSD reacted, and the levels that decide the path toward 4,000.",
    category: "Macro Analysis",
    author: "XAU Pro Academy Research Desk",
    publishedAt: "2026-10-14",
    updatedAt: "2026-10-14",
    readTime: "6 min",
    heroImage: "/images/articles/us-cpi-september-2026-gold-cover.svg",
    coverGradient: "from-amber-900/40 via-card to-slate-900",
    metaDescription:
      "US CPI for September 2026 came in hot: 3.7% y/y vs 3.6% expected, core 0.3% m/m vs 0.2%. What the numbers mean for the Fed, real yields and XAUUSD, with key levels.",
    focusKeyword: "CPI gold price",
    secondaryKeywords: ["US CPI September 2026", "core CPI", "gold after CPI", "XAUUSD CPI reaction", "gold 4000"],
    keyTakeaways: [
      "Headline CPI rose to 3.7% y/y (forecast 3.6%, previous 3.4%). Inflation is accelerating.",
      "Core CPI held at 0.3% m/m against a 0.2% forecast and rose to 2.6% y/y. Pressure is spreading beyond energy.",
      "A hot core print supports higher real yields and a firmer Dollar, a macro headwind for gold.",
      "Gold dropped from 4,152 to near 4,110 within minutes, breaking 4,120 with body closes. Next references are 4,085 and 4,000.",
    ],
    toc: [
      { id: "the-print", label: "The Print" },
      { id: "what-the-numbers-mean", label: "What the Numbers Mean" },
      { id: "first-reaction", label: "How Gold Reacted" },
      { id: "why-cpi-moves-gold", label: "Why CPI Moves Gold" },
      { id: "scenarios", label: "Scenarios From Here" },
      { id: "cpi-gold-matrix", label: "CPI and Gold at a Glance" },
      { id: "structure", label: "Structural Confirmation" },
      { id: "risk", label: "Risk Management" },
      { id: "conclusion", label: "Conclusion" },
      { id: "faq", label: "Frequently Asked Questions" },
    ],
    faq: [
      { q: "Was the September 2026 CPI higher than expected?", a: "Yes. Headline CPI rose 3.7% year over year against a 3.6% forecast, and core CPI rose 0.3% month over month against 0.2%. Core year over year reached 2.6% versus 2.5% expected. Only headline month over month matched the 0.6% forecast." },
      { q: "Why does core CPI matter more than headline for gold?", a: "Core excludes food and energy, so it shows whether inflation is spreading into underlying prices. The Federal Reserve sets policy around it. A hot core reading pushes rate cut expectations further out, lifts real yields and supports the Dollar, which pressures gold." },
      { q: "Can gold fall to 4,000 after this CPI report?", a: "The data supports that risk but does not confirm it. Sell-side liquidity rests near 4,140 to 4,120. A body close below that zone with displacement would open 4,050 and the 4,000 psychological level. Without that close, the hot print may already be priced." },
      { q: "How far did gold fall after the September 2026 CPI?", a: "XAUUSD fell from 4,152 before the release to near 4,110 within seven minutes, a drop of about 42 dollars. The first candle was quiet while spreads were wide, then sellers broke the 4,140 to 4,120 liquidity with body closes." },
      { q: "What would turn gold bullish after this CPI?", a: "If yields fade and the Dollar softens despite the print, a sweep and acceptance above buy-side liquidity at 4,175 to 4,200 would be the first structural sign that the correction is losing control." },
    ],
    relatedSlugs: ["gold-correction-5600-peak-macro-analysis", "smc-gold-trading-strategy", "roadmap-2026-gold-trading"],
    Content: () => import("@/content/articles/us-cpi-september-2026-gold-reaction").then((m) => ({ default: m.UsCpiSeptember2026Content })),
  },
  {
    slug: "gold-retail-sentiment-contrarian-liquidity",
    title: "Gold Retail Sentiment: Why Contrarian SSI Fails Without Liquidity",
    seoTitle: "Gold Retail Sentiment and Contrarian SSI",
    excerpt:
      "Retail sentiment on XAU/USD is a map of stop clusters, not an entry signal. How to read SSI as inducement and confirm with a sweep, displacement and structure shift.",
    category: "SMC Foundations",
    author: "XAU Pro Academy Research Desk",
    publishedAt: "2026-10-06",
    updatedAt: "2026-10-06",
    readTime: "7 min",
    heroImage: goldRetailSentimentCover.url,
    coverGradient: "from-gold-muted via-card to-slate-900",
    metaDescription:
      "Gold retail sentiment explained. Why contrarian XAUUSD SSI trading fails, how sentiment maps buy-side and sell-side liquidity, and a 3-step confirmation checklist.",
    focusKeyword: "Gold Retail Sentiment",
    secondaryKeywords: [
      "XAUUSD SSI",
      "Contrarian gold sentiment",
      "Gold sentiment liquidity",
      "Retail sentiment trap",
    ],
    keyTakeaways: [
      "Sentiment shows where retail exposure sits, not when it will be liquidated.",
      "A crowded short side places buy stops above highs: that is buy-side liquidity price often takes first.",
      "Enter only after a sweep, displacement and an MSS or CHoCH aligned with H1 and H4.",
      "Neither retail SSI nor the COT report is an entry trigger. Both are context.",
    ],
    toc: [
      { id: "introduction", label: "Sentiment Is a Map, Not a Trigger" },
      { id: "naive-contrarian", label: "Why Naive Contrarian Trading Fails" },
      { id: "sentiment-as-liquidity", label: "Reading SSI as Liquidity" },
      { id: "checklist", label: "Three-Step Confirmation Checklist" },
      { id: "ssi-vs-cot", label: "Retail SSI vs COT Data" },
      { id: "comparison", label: "Naive Contrarian vs Liquidity Framework" },
      { id: "final-thoughts", label: "Final Thoughts" },
      { id: "faq", label: "Frequently Asked Questions" },
    ],
    faq: [
      {
        q: "What is gold retail sentiment?",
        a: "It is the share of a broker's retail accounts holding long or short XAU/USD positions. It describes crowd exposure, not future direction or timing.",
      },
      {
        q: "Should I trade gold against retail sentiment?",
        a: "Not on the reading alone. Extreme readings can persist for weeks during strong trends. Use sentiment to locate likely stop clusters, then wait for a sweep, displacement and a structure shift.",
      },
      {
        q: "What does XAUUSD SSI mean?",
        a: "SSI stands for Speculative Sentiment Index, a broker-published ratio of retail long to short positions on gold.",
      },
      {
        q: "Is the COT report better than retail sentiment?",
        a: "It covers all reportable COMEX gold futures traders and suits medium term context, but it is weekly. Neither dataset is an entry signal.",
      },
    ],
    relatedSlugs: [
      "liquidity-sweep-gold-trading",
      "inducement-trap-idm-smc",
      "sentiment-dashboards-vs-smc-execution-gold",
      "choch-vs-bos-smart-money",
    ],
    Content: () =>
      import("@/content/articles/gold-retail-sentiment-contrarian-liquidity").then((m) => ({
        default: m.GoldRetailSentimentContent,
      })),
  },
  {
    slug: "sentiment-dashboards-vs-smc-execution-gold",
    title: "Sentiment Dashboards vs Structural Execution: Why Macro Scores Fail Gold Entries",
    seoTitle: "Sentiment Dashboards vs Gold Structure",
    excerpt:
      "COT, retail sentiment and seasonality scores tell you which side to favor. They do not tell you where to enter or where you are wrong. Here is how structural SMC execution closes that gap on XAUUSD.",
    category: "Strategy",
    author: "XAU Pro Academy Research Desk",
    publishedAt: "2026-10-05",
    updatedAt: "2026-10-05",
    readTime: "9 min",
    heroImage: sentimentVsSmcCover.url,
    coverGradient: "from-amber-900/40 via-card to-slate-900",
    metaDescription:
      "Why COT data, retail sentiment and macro scorecards fail as gold entry tools, and how structural SMC execution with liquidity, displacement and fixed risk produces verifiable XAUUSD setups.",
    focusKeyword: "sentiment dashboard vs SMC gold",
    secondaryKeywords: [
      "COT data gold trading",
      "retail sentiment indicator XAUUSD",
      "macro scorecard trading",
      "SMC execution gold",
      "high probability gold setups",
      "how to verify trading signals",
    ],
    keyTakeaways: [
      "Sentiment and COT scores describe direction, not timing, invalidation or risk.",
      "Gold can stay macro bullish while printing repeated 40 to 80 USD intraday declines.",
      "Structural execution requires liquidity interaction, displacement and a qualified POI.",
      "Fixed risk geometry makes results measurable in R and compatible with funded accounts.",
      "Verify any service through a complete trade-by-trade history, not screenshots.",
    ],
    toc: [
      { id: "introduction", label: "Direction Is Not an Entry" },
      { id: "what-dashboards-measure", label: "What Dashboards Measure" },
      { id: "timing-gap", label: "The Timing Gap on XAUUSD" },
      { id: "structural-execution", label: "Structural Execution" },
      { id: "side-by-side", label: "Side by Side Comparison" },
      { id: "risk-geometry", label: "Fixed Risk Geometry" },
      { id: "verify-claims", label: "How to Verify a Service" },
      { id: "combining", label: "Combining Context With Execution" },
      { id: "final-thoughts", label: "Final Thoughts" },
      { id: "faq", label: "Frequently Asked Questions" },
    ],
    faq: [
      {
        q: "Is COT data useful for trading gold?",
        a: "Yes, as slow context. COT reports are weekly and reflect positions from the previous Tuesday, so they describe speculative positioning trends, not intraday entries.",
      },
      {
        q: "Why do I lose trades when the gold sentiment score is bullish?",
        a: "Because a bullish backdrop does not prevent intraday liquidity raids. Entries taken without structural confirmation are often placed directly into the sell-side liquidity that price targets before moving higher.",
      },
      {
        q: "What makes an SMC setup high probability?",
        a: "Higher timeframe alignment, a liquidity sweep, displacement with a body close, an unmitigated POI created by that displacement, and supportive session context. Missing elements lower the grade or reject the setup.",
      },
      {
        q: "Can I combine macro dashboards with SMC?",
        a: "Yes. Use macro and sentiment to define the backdrop, then let structure decide whether a trade exists. When they disagree, standing aside is a valid decision.",
      },
      {
        q: "How can I check if a trading service is genuinely profitable?",
        a: "Ask for a complete trade-by-trade record including losses, fixed rules defined in advance, maximum drawdown and losing streak, and a clear separation between trading results and product sales.",
      },
    ],
    relatedSlugs: [
      "smc-vs-indicators-gold-trading",
      "high-probability-entry-criteria-gold",
      "liquidity-sweep-gold-trading",
      "funded-mindset-risk-management",
    ],
    Content: () =>
      import("@/content/articles/sentiment-dashboards-vs-smc-execution-gold").then((m) => ({
        default: m.SentimentVsSmcContent,
      })),
  },
  {
    slug: "gold-correction-5600-peak-macro-analysis",
    title: "Why Gold Keeps Falling After Its Historic $5,600 Peak",
    seoTitle: "Why Gold Keeps Falling After $5,600",
    excerpt:
      "Gold’s correction from $5,600 toward $4,480 is not a fear unwind. It is a full repricing of real yields, Fed policy, and the Hormuz inflation channel. Here is what smart money is actually watching.",
    category: "Macro Analysis",
    author: "XAU Pro Academy Research Desk",
    publishedAt: "2026-05-18",
    updatedAt: "2026-05-18",
    readTime: "8 min",
    coverGradient: "from-amber-900/40 via-card to-slate-900",
    metaDescription:
      "Why gold sold off from its $5,600 all-time high toward $4,480. A macro breakdown of real yields, Fed transition risk, the Hormuz inflation shock, and what institutions are watching next.",
    focusKeyword: "Gold price correction 2026",
    secondaryKeywords: [
      "XAU/USD macro analysis",
      "Why is gold falling",
      "Fed policy gold price",
      "Strait of Hormuz oil",
      "Real yields gold",
      "Gold $5600 peak",
    ],
    heroImage: goldCorrection5600HeroImage,
    keyTakeaways: [
      "Gold’s drop from $5,600 toward $4,480 is a macro repricing, not a technical correction.",
      "Higher real yields and a stronger USD are the dominant drivers, not falling geopolitical risk.",
      "The Hormuz crisis has shifted from a safe-haven catalyst to an inflation transmission mechanism.",
      "Watch real yields rolling over: that is historically the cleanest signal of a gold cycle low.",
    ],
    toc: [
      { id: "introduction", label: "A Historic Peak, Then a Sharp Repricing" },
      { id: "not-just-fear", label: "Beyond Geopolitical Fear" },
      { id: "fed-transition", label: "The Fed Transition Risk" },
      { id: "hormuz-inflation", label: "Hormuz and Inflation" },
      { id: "macro-board", label: "What Smart Money Is Watching" },
      { id: "bigger-risk", label: "The Risk Not Priced Yet" },
      { id: "outlook", label: "Strategic Outlook" },
      { id: "final-thoughts", label: "Final Thoughts" },
      { id: "faq", label: "Frequently Asked Questions" },
    ],
    faq: [
      {
        q: "Why is gold falling if there are still wars and geopolitical risks?",
        a: "Because gold is no longer trading on fear alone. Markets are repricing the monetary consequences of those risks. Energy-driven inflation forces central banks to stay restrictive, which lifts real yields and the US Dollar. Higher real yields raise the opportunity cost of holding a non-yielding asset like gold, so institutional money rotates out even while geopolitical risk stays elevated.",
      },
      {
        q: "What level matters now after the drop from $5,600?",
        a: "From a macro perspective, the $4,480 zone is the area where institutional positioning has been most active. But price levels matter less than the real-yield trend. A sustained rollover in 10-year US TIPS yields, combined with softer growth data, would be a stronger bottoming signal than any single price level.",
      },
      {
        q: "Are real yields really more important than inflation for gold?",
        a: "Yes. Headline inflation alone does not determine gold direction. What matters is real yields, which is nominal yields minus inflation expectations. When real yields rise faster than inflation expectations, gold tends to underperform regardless of how scary the headlines look. This is the single most reliable macro driver of XAU/USD over multi-month cycles.",
      },
      {
        q: "How does the Federal Reserve transition affect gold?",
        a: "Markets dislike policy uncertainty more than bad news. A change in Fed leadership during an inflationary, debt-heavy regime raises questions about credibility, the pace of easing, and tolerance for above-target inflation. Until the new framework is clear, markets default to assuming continuity of restrictive policy, which keeps the USD bid and gold pressured.",
      },
      {
        q: "Is this the end of the gold bull market?",
        a: "Not based on current structural conditions. The drivers behind the rally to $5,600, sovereign debt risk, central-bank accumulation, geopolitical fragmentation, and currency debasement, are unresolved. This phase looks more like a macro reset within a long-term bull cycle than a structural top. Gold cycle reversals tend to be abrupt once real yields peak.",
      },
    ],
    relatedSlugs: [
      "roadmap-2026-gold-trading",
      "smc-gold-trading-strategy",
      "what-is-smart-money-concepts",
      "smc-vs-indicators-gold-trading",
    ],
    Content: () => import("@/content/articles/gold-correction-5600-peak-macro-analysis").then((m) => ({ default: m.GoldCorrection5600Content })),
  },
  {
    slug: "smc-gold-trading-strategy",
    title: "SMC Gold Trading Strategy for XAU/USD",
    seoTitle: "SMC Gold Trading Strategy: H4 Bias to M15 Entry",
    excerpt:
      "Master the XAU/USD market with the ultimate SMC Gold Trading Strategy. Learn how to identify institutional footprints, liquidity sweeps, and high-probability entries.",
    category: "SMC Foundations",
    author: "XAU Pro Academy Team",
    publishedAt: "2026-04-27",
    updatedAt: "2026-10-22",
    readTime: "9 min",
    coverGradient: "from-gold-muted via-card to-emerald-muted",
    metaDescription:
      "A step by step SMC strategy for gold: read H4 bias, wait for the M15 liquidity sweep, enter at the FVG or order block, and cap risk at 1%. Free XAUUSD guide.",
    focusKeyword: "SMC Gold Trading Strategy",
    secondaryKeywords: [
      "XAU/USD Mastery",
      "Smart Money Concepts Gold",
      "Institutional Trading XAUUSD",
    ],
    keyTakeaways: [
      "Gold moves to liquidity, not to indicators. Stop-hunts are the entry signal, not the trap.",
      "The three SMC pillars on XAU/USD: Order Blocks, Fair Value Gaps, and Break/Change of Character.",
      "Best execution flow: H4 bias → M15 liquidity sweep → entry at FVG or Order Block → target the next pool.",
      "Edge comes from patience and risk control, not from prediction. Cap risk at 1% per trade.",
    ],
    toc: [
      { id: "introduction", label: "Why Most Gold Traders Fail" },
      { id: "phase-1", label: "Phase 1: The Gold Character" },
      { id: "phase-2", label: "Phase 2: Core SMC Pillars" },
      { id: "phase-3", label: "Phase 3: The XAU Pro Setup" },
      { id: "conclusion", label: "Discipline Over Hype" },
      { id: "faq", label: "Frequently Asked Questions" },
    ],
    faq: [
      {
        q: "What is Smart Money Concepts (SMC)?",
        a: "SMC is a price-action methodology that maps how institutional traders move markets through liquidity, order blocks, and fair value gaps, instead of relying on lagging indicators like RSI or moving averages.",
      },
      {
        q: "Is SMC profitable on XAU/USD?",
        a: "Gold's volatility and liquidity make it one of the cleanest instruments for SMC. Stop-hunts, sweeps, and structure shifts are visible daily on XAU/USD, but profitability still depends on disciplined execution and strict risk management.",
      },
      {
        q: "What's the best timeframe for SMC on gold?",
        a: "We use the H4 chart to define the directional bias and the M15 chart to time entries after a liquidity sweep. Lower timeframes work but increase noise and emotional decisions.",
      },
      {
        q: "Order Block vs Fair Value Gap, which should I trade?",
        a: "Both are valid. Order Blocks mark where institutions placed orders; FVGs mark price inefficiencies that often get filled. The highest-probability entries occur where an FVG sits inside or next to an Order Block, with a confirmed CHoCH on M15.",
      },
      {
        q: "How long does it take to master SMC on gold?",
        a: "Expect 3–6 months of focused study and journaled trades to build pattern recognition. The concepts are simple; the execution discipline is what takes time.",
      },
    ],
    relatedSlugs: [
      "smc-mistakes-prop-firm-challenge",
      "amd-pattern-gold-daily-cycles",
      "roadmap-2026-gold-trading",
      "smc-vs-indicators-gold-trading",
    ],
    Content: () => import("@/content/articles/smc-gold-trading-strategy").then((m) => ({ default: m.SmcGoldStrategyContent })),
  },
  {
    slug: "smc-mistakes-prop-firm-challenge",
    title: "5 SMC Mistakes Failing Prop Firm Traders",
    excerpt:
      "Stop failing your trading evaluations. Discover the 5 most common SMC mistakes that lead to drawdown violations and learn how to pass your Prop Firm challenge on Gold.",
    category: "Prop Firm Trading",
    author: "XAU Pro Academy Team",
    publishedAt: "2026-04-27",
    updatedAt: "2026-04-27",
    readTime: "7 min",
    coverGradient: "from-gold-muted via-card to-emerald-muted",
    metaDescription:
      "Stop failing trading evaluations. The 5 SMC mistakes that cause drawdown violations, and how to pass your Prop Firm challenge on Gold.",
    focusKeyword: "Prop Firm Challenge SMC",
    secondaryKeywords: [
      "SMC Trading Mistakes",
      "Pass Prop Firm Challenge Gold",
      "XAUUSD Prop Firm Strategy",
    ],
    keyTakeaways: [
      "High-timeframe (HTF) bias is non-negotiable for Prop Firm consistency.",
      "\"Micro-pip\" stop losses are the fastest way to hit your daily drawdown limit on Gold.",
      "The Power of Three (AMD) is the key to avoiding retail traps.",
      "Risk management is the only \"Holy Grail\" in funded trading.",
    ],
    toc: [
      { id: "introduction", label: "The Prop Firm Dream vs. Reality" },
      { id: "mistake-1", label: "Mistake #1: Ignoring HTF Narrative" },
      { id: "mistake-2", label: "Mistake #2: Tight Stop Losses on Gold" },
      { id: "mistake-3", label: "Mistake #3: Trading the Initial Move" },
      { id: "mistake-4", label: "Mistake #4: Revenge Trading" },
      { id: "mistake-5", label: "Mistake #5: Misunderstanding FVGs" },
      { id: "conclusion", label: "The Roadmap to Funding" },
      { id: "faq", label: "Frequently Asked Questions" },
    ],
    faq: [
      {
        q: "Can I use SMC to pass a 50K Prop Firm challenge?",
        a: "Absolutely. SMC is one of the strongest methodologies for Prop Firms because it focuses on high-probability setups, clean structure-based entries, and clear risk-to-reward ratios, all of which align with strict drawdown rules.",
      },
      {
        q: "Which Prop Firm is best for Gold trading?",
        a: "Look for firms with consistently low XAU/USD spreads, fast execution, weekend-holding allowed, and a verifiable payout history. Avoid firms with vague rules or hidden consistency clauses, read the rulebook line by line before paying for any challenge.",
      },
      {
        q: "How much should I risk per trade?",
        a: "For a Prop Firm challenge, risk no more than 0.5% to 1% per trade. This lets you survive a 4–5 trade losing streak without breaching the daily or overall drawdown limits.",
      },
      {
        q: "What's the #1 reason traders fail Prop Firm challenges?",
        a: "Emotional decisions after a losing trade, revenge trading, oversizing, and abandoning the plan. The strategy is rarely the problem; the inability to stop trading at a pre-set daily loss is what blows almost every account.",
      },
      {
        q: "Should I practice on a demo before paying for a challenge?",
        a: "Yes. Journal at least 50 trades on a demo account that mirrors the firm's exact rules (account size, daily drawdown, max drawdown). If you can't pass on demo with consistent risk, paying for a live challenge will only speed up the loss.",
      },
    ],
    relatedSlugs: [
      "amd-pattern-gold-daily-cycles",
      "smc-gold-trading-strategy",
      "drawdown-psychology-funded-accounts",
      "roadmap-2026-gold-trading",
    ],
    Content: () => import("@/content/articles/smc-mistakes-prop-firm-challenge").then((m) => ({ default: m.SmcPropFirmMistakesContent })),
  },
  {
    slug: "smc-vs-indicators-gold-trading",
    title: "SMC vs Indicators for Gold Traders",
    excerpt:
      "Stop relying on lagging indicators. Learn why Smart Money Concepts outshine RSI, MACD, and Moving Averages for high-precision XAU/USD trading.",
    category: "Strategy",
    author: "XAU Pro Academy Team",
    publishedAt: "2026-04-27",
    updatedAt: "2026-04-27",
    readTime: "6 min",
    coverGradient: "from-gold-muted via-card to-emerald-muted",
    metaDescription:
      "Stop relying on lagging indicators. Why Smart Money Concepts (SMC) outshine RSI and MACD for high-precision Gold trading.",
    focusKeyword: "SMC vs Indicators Gold",
    secondaryKeywords: [
      "XAUUSD Technical Analysis",
      "Why indicators fail",
      "Price Action vs Indicators",
      "Best Gold Trading Tools",
    ],
    keyTakeaways: [
      "Indicators are mathematical averages of past price action.",
      "Gold's liquidity is driven by institutional orders, not RSI levels.",
      "SMC focuses on the \"Cause\" (Liquidity) rather than the \"Effect\" (Price movement).",
      "High-probability setups combine Market Structure with institutional footprints.",
    ],
    toc: [
      { id: "introduction", label: "The Lagging Trap" },
      { id: "problem-with-indicators", label: "The Problem with Indicators" },
      { id: "smc-leading-alternative", label: "Why SMC is Leading" },
      { id: "case-study", label: "Gold Case Study" },
      { id: "hybrid-approach", label: "The Hybrid Approach" },
      { id: "conclusion", label: "Clean Your Charts" },
      { id: "faq", label: "Frequently Asked Questions" },
    ],
    faq: [
      {
        q: "Are indicators completely useless for Gold?",
        a: "Not useless, but they are lagging. They can confirm a trend that's already in motion, but they're poor at predicting precise reversals compared to SMC Order Blocks and Liquidity Sweeps.",
      },
      {
        q: "Is SMC harder to learn than indicator trading?",
        a: "It has a steeper learning curve because it requires understanding market psychology and order flow rather than just \"waiting for a cross.\" The trade-off is far higher precision and a real understanding of why price moves.",
      },
      {
        q: "Why does Gold ignore RSI \"Overbought\" levels so often?",
        a: "Gold is a trending asset driven by global macro events and liquidity. If there is a massive pool of stops sitting higher up, price will ignore the RSI until that liquidity is neutralized, the algorithm hunts stops, not oscillator readings.",
      },
      {
        q: "What is the best \"Leading\" indicator for XAU/USD?",
        a: "The best leading indicator is Price Action combined with Volume. Specifically, watch how price reacts at institutional Supply and Demand zones after a liquidity sweep, that reaction tells you what big players are doing in real time.",
      },
      {
        q: "Can I combine SMC with Moving Averages?",
        a: "Yes. Some traders use the 200 EMA as a directional filter for higher-timeframe bias, then use SMC concepts (Order Blocks, FVGs, CHoCH) for precise entries on the lower timeframes. Just never let the EMA override clean structure.",
      },
    ],
    relatedSlugs: [
      "smc-gold-trading-strategy",
      "smc-mistakes-prop-firm-challenge",
      "amd-pattern-gold-daily-cycles",
      "roadmap-2026-gold-trading",
    ],
    Content: () => import("@/content/articles/smc-vs-indicators-gold-trading").then((m) => ({ default: m.SmcVsIndicatorsContent })),
  },
  {
    slug: "drawdown-psychology-funded-accounts",
    title: "Drawdown Psychology on Funded Accounts: A Practical Guide",
    seoTitle: "Funded Account Drawdown Psychology Guide",
    excerpt:
      "A practical XAUUSD guide to loss aversion, daily equity headroom and decision quality under pressure, with worked account examples and a structured review protocol.",
    category: "Psychology",
    author: "XAU Pro Academy Team",
    publishedAt: "2026-04-27",
    updatedAt: "2026-10-13",
    readTime: "12 min",
    coverGradient: "from-gold-muted via-card to-emerald-muted",
    metaDescription:
      "Manage funded account drawdown with loss-aversion guidance, a $100,000 risk example, equity headroom checks and a practical XAUUSD decision protocol.",
    focusKeyword: "Trading Drawdown Psychology",
    secondaryKeywords: [
      "Prop Firm Drawdown Management",
      "Funded Account Daily Loss Limit",
      "Loss Aversion in Trading",
      "Gold Trading Psychology",
    ],
    keyTakeaways: [
      "Daily and overall equity headroom can constrain risk long before the headline account balance suggests a problem.",
      "A breakeven deadline is not a setup qualification criterion or a reason to increase exposure.",
      "Review execution deviations separately from qualified trades that lose.",
      "Precommit risk tiers and pause conditions; a short winning streak does not prove readiness to restore size.",
      "Losing-sequence calculations are illustrations, not guarantees about the next trade.",
    ],
    toc: [
      { id: "introduction", label: "Protect the next decision" },
      { id: "measure-drawdown", label: "Drawdown vs. headroom" },
      { id: "loss-aversion", label: "Loss aversion" },
      { id: "breakeven-obsession", label: "Recovery arithmetic" },
      { id: "worked-example", label: "$100,000 account example" },
      { id: "decision-protocol", label: "Decision protocol" },
      { id: "probability-thinking", label: "Losing sequences" },
      { id: "journal-review", label: "Journal and resumption" },
      { id: "conclusion", label: "Conclusion" },
      { id: "sources", label: "Sources and scope" },
      { id: "faq", label: "Frequently Asked Questions" },
    ],
    faq: [
      {
        q: "How can I manage fear after a losing sequence on a funded account?",
        a: "First reconcile equity and remaining loss headroom. Review whether prior losses followed the plan or involved execution deviations. Use prewritten pause and risk-tier rules rather than increasing size to recover. Persistent distress is a reason to step away and seek qualified support.",
      },
      {
        q: "Should I stop trading at a 3% drawdown?",
        a: "There is no universal 3% threshold. The decision depends on the account product, applicable equity floors, open exposure, execution conditions and your precommitted plan. Stop adding risk when headroom is insufficient, calculations are uncertain or a process deviation remains unresolved.",
      },
      {
        q: "Do floating losses count toward a prop firm's daily loss limit?",
        a: "They can. Equity-based rules may include floating results, commissions and swaps. The exact calculation and daily reset time depend on the provider and product, so verify the current official terms rather than relying only on account balance.",
      },
      {
        q: "How much gain is required to recover a 5% account loss?",
        a: "A 5% loss requires approximately a 5.26% gain on the remaining equity to return to the reference value, excluding costs and cash flows. The formula is loss percentage divided by 100 minus loss percentage, multiplied by 100. It does not estimate recovery time.",
      },
      {
        q: "Do four consecutive losses mean the strategy is broken?",
        a: "Not by themselves. In a simplified independent-trade model with a constant 60% win probability, the next four specified trades all lose with 2.56% probability. Real trades can be correlated and historical win rates are uncertain, so review setup quality, costs and regime changes rather than assuming the next trade must win.",
      },
      {
        q: "Should I hide my P&L during a drawdown?",
        a: "Do not hide information needed to monitor equity, open exposure or loss limits. Separate risk monitoring from entry selection so every P&L fluctuation does not trigger an unplanned change in size or criteria. Use account alerts and a scheduled review while retaining necessary oversight.",
      },
    ],
    relatedSlugs: [
      "drawdown-recovery-strategy",
      "funded-mindset-risk-management",
      "smc-mistakes-prop-firm-challenge",
      "smc-trading-strategy-gold",
    ],
    Content: () => import("@/content/articles/drawdown-psychology-funded-accounts").then((m) => ({ default: m.DrawdownPsychologyContent })),
  },
  {
    slug: "amd-pattern-gold-daily-cycles",
    title: "AMD Pattern: Gold's Daily Cycles",
    excerpt:
      "Unlock the secret of institutional price delivery. Master the Accumulation, Manipulation, and Distribution (AMD) pattern to catch high-probability moves on Gold.",
    category: "SMC Foundations",
    author: "XAU Pro Academy Team",
    publishedAt: "2026-04-27",
    updatedAt: "2026-04-27",
    readTime: "6 min",
    coverGradient: "from-gold-muted via-card to-emerald-muted",
    metaDescription:
      "Unlock institutional price delivery. Master the Accumulation, Manipulation, Distribution pattern to catch high-probability Gold moves.",
    focusKeyword: "AMD Pattern SMC Gold",
    secondaryKeywords: [
      "Accumulation Manipulation Distribution",
      "XAUUSD Daily Cycle",
      "London Session Trap",
      "Power of Three Trading",
    ],
    keyTakeaways: [
      "Accumulation: the phase where retail orders are collected inside a tight range.",
      "Manipulation: the \"Judas Swing\" designed to trap breakout traders on the wrong side.",
      "Distribution: the real trend where institutions push price to their true target.",
      "Trading Gold without identifying the AMD phase is pure gambling.",
    ],
    toc: [
      { id: "introduction", label: "The Market's Daily Heartbeat" },
      { id: "accumulation", label: "Phase 1: Accumulation" },
      { id: "manipulation", label: "Phase 2: Manipulation" },
      { id: "distribution", label: "Phase 3: Distribution" },
      { id: "judas-swing", label: "The Judas Swing on Gold" },
      { id: "conclusion", label: "Patience is Profit" },
      { id: "faq", label: "Frequently Asked Questions" },
    ],
    faq: [
      {
        q: "Does the AMD pattern happen every day?",
        a: "It's very common on Gold, especially during high-volatility days. On strong trending days you may see a re-accumulation instead of a full AMD cycle, but the underlying institutional logic is the same.",
      },
      {
        q: "Which timeframe is best for identifying AMD?",
        a: "Use the H1 or M15 to identify the Accumulation and Manipulation phases, then drop to M1 or M5 for your precise entry during the Distribution phase.",
      },
      {
        q: "How do I know if a breakout is real or just manipulation?",
        a: "Manipulation is usually fast and followed by a strong displacement (large candles) in the opposite direction. A real breakout shows healthy pullbacks and holds the new levels as support or resistance.",
      },
      {
        q: "What session is best for AMD on Gold?",
        a: "The London/New York overlap, often called the \"Power Hour\", is the most reliable window for the Manipulation and Distribution phases to play out on XAU/USD.",
      },
      {
        q: "Can AMD be used on other pairs like GBPUSD?",
        a: "Yes. AMD is a universal institutional delivery model and works exceptionally well on major pairs like GBPUSD and EURUSD, as well as indices like NAS100 and US30.",
      },
    ],
    relatedSlugs: [
      "smc-gold-trading-strategy",
      "smc-mistakes-prop-firm-challenge",
      "roadmap-2026-gold-trading",
      "drawdown-psychology-funded-accounts",
    ],
    Content: () => import("@/content/articles/amd-pattern-gold-daily-cycles").then((m) => ({ default: m.AmdPatternContent })),
  },
  {
    slug: "roadmap-2026-gold-trading",
    title: "2026 Gold Trading Roadmap",
    excerpt:
      "Your daily operational plan for XAU/USD. Learn how to combine high-impact news with SMC and master the daily routine of a professional Gold trader.",
    category: "Trading Plan",
    author: "XAU Pro Academy Team",
    publishedAt: "2026-04-27",
    updatedAt: "2026-04-27",
    readTime: "7 min",
    coverGradient: "from-gold-muted via-card to-emerald-muted",
    metaDescription:
      "Your daily operational plan for XAU/USD. Combine high-impact news with SMC and master the daily routine of a professional Gold trader.",
    focusKeyword: "Gold Trading Roadmap 2026",
    secondaryKeywords: [
      "XAUUSD News Trading",
      "High Impact News SMC",
      "Trading Plan for Gold",
      "SMC Session Timing",
    ],
    keyTakeaways: [
      "Never trade into high-impact news, trade the reaction to it.",
      "The hour before and after the New York Open is the \"Golden Window.\"",
      "Your routine matters more than your strategy on any given day.",
      "Confluence is the key: News + Liquidity + Market Structure.",
    ],
    toc: [
      { id: "introduction", label: "From Theory to Execution" },
      { id: "daily-routine", label: "1. The Daily Routine" },
      { id: "news", label: "2. Navigating News" },
      { id: "time-windows", label: "3. Time-Price Windows" },
      { id: "integration", label: "4. Integration" },
      { id: "conclusion", label: "Discipline is the Bridge" },
      { id: "faq", label: "Frequently Asked Questions" },
    ],
    faq: [
      {
        q: "Should I trade during the NFP (Non-Farm Payrolls)?",
        a: "For most traders, it's best to wait 15–30 minutes after the release. Let the initial \"noise\" clear, then wait for an SMC setup to form on the M5 timeframe before considering an entry.",
      },
      {
        q: "What is the most important news for Gold in 2026?",
        a: "Inflation data (CPI), Central Bank interest rate decisions (FOMC), and geopolitical headlines remain the primary drivers for XAU/USD.",
      },
      {
        q: "Can I use this roadmap for scalp trading?",
        a: "Absolutely. By focusing on the New York Overlap and using the M1 for entries, this roadmap is well suited for high-frequency SMC scalping.",
      },
      {
        q: "How many trades should I take per day?",
        a: "Quality over quantity. One or two high-quality AMD or Liquidity Sweep setups per day are more than enough to grow a funded account consistently.",
      },
      {
        q: "Do I need to watch the DXY (US Dollar Index) while trading Gold?",
        a: "Yes. Gold typically has an inverse relationship with the DXY. If DXY is rejecting a major Resistance or Order Block, look for a bullish setup on Gold, and vice versa.",
      },
    ],
    relatedSlugs: [
      "amd-pattern-gold-daily-cycles",
      "smc-vs-indicators-gold-trading",
      "drawdown-psychology-funded-accounts",
      "smc-gold-trading-strategy",
    ],
    Content: () => import("@/content/articles/roadmap-2026-gold-trading").then((m) => ({ default: m.Roadmap2026Content })),
  },
  {
    slug: "liquidity-purge-eqh-eql-gold",
    title: "Gold Liquidity: EQH, EQL and the Purge",
    excerpt:
      "Learn why 90% of retail traders get stopped out before the real move. Master Equal Highs, Equal Lows, and the institutional liquidity grab on XAU/USD.",
    category: "Advanced Liquidity",
    author: "XAU Pro Academy Team",
    publishedAt: "2026-04-27",
    updatedAt: "2026-04-27",
    readTime: "7 min",
    coverGradient: "from-gold-muted via-card to-emerald-muted",
    metaDescription:
      "Master XAU/USD liquidity hunts. How Equal Highs, Equal Lows, and stop-grabs drive every major Gold move, and how to trade the reversal.",
    focusKeyword: "Gold Liquidity Grab",
    secondaryKeywords: [
      "Equal Highs Equal Lows",
      "XAUUSD Liquidity Sweep",
      "Buy-Side Sell-Side Liquidity",
      "Stop Hunt Gold",
    ],
    keyTakeaways: [
      "Equal Highs and Equal Lows are magnets for institutional liquidity hunts.",
      "Liquidity sits where retail stops live, above old highs and below old lows.",
      "The signal is the reaction after the sweep, not the sweep itself.",
      "Enter on the CHoCH after the grab, target the next opposite liquidity pool.",
    ],
    toc: [
      { id: "introduction", label: "Why 90% Get Stopped Out First" },
      { id: "eqh-eql", label: "Equal Highs & Equal Lows" },
      { id: "why-liquidity-sits", label: "Why Liquidity Sits There" },
      { id: "liquidity-grab", label: "The Liquidity Grab" },
      { id: "xauusd-example", label: "XAU/USD Example" },
      { id: "trading-the-reversal", label: "Trading the Reversal" },
      { id: "wrap-up", label: "Wrap-Up" },
      { id: "faq", label: "Frequently Asked Questions" },
    ],
    faq: [
      {
        q: "What's the difference between buy-side and sell-side liquidity?",
        a: "Buy-side liquidity sits above old highs (where breakout buy stops and short stops are). Sell-side liquidity sits below old lows (where breakout sell stops and long stops are). Smart Money targets whichever pool is closer and larger.",
      },
      {
        q: "How many pips does a liquidity grab need to be valid on Gold?",
        a: "On XAU/USD, a clean grab usually pierces the level by 10–40 pips and immediately reverses with strong displacement. If price closes beyond the level and consolidates, it's a real breakout, not a grab.",
      },
      {
        q: "Can I trade the sweep itself or only the reversal?",
        a: "Trade only the reversal. Anticipating the exact sweep level leads to early entries and stop-outs. Wait for the wick, the CHoCH, and a clean retrace into the displacement OB or FVG.",
      },
      {
        q: "Which timeframe is best for spotting EQH/EQL?",
        a: "Mark them on H1 and H4 for the highest-quality pools. M15 EQH/EQL also work for intraday setups but generate more false signals.",
      },
    ],
    relatedSlugs: [
      "inducement-trap-idm-smc",
      "high-probability-entry-criteria-gold",
      "smc-gold-trading-strategy",
      "amd-pattern-gold-daily-cycles",
    ],
    Content: () => import("@/content/articles/liquidity-purge-eqh-eql-gold").then((m) => ({ default: m.LiquidityPurgeContent })),
  },
  {
    slug: "inducement-trap-idm-smc",
    title: "Inducement vs Real Structure on Gold",
    excerpt:
      "Smart Money 'induces' retail traders to enter early just to take their stops. Learn to spot the IDM trap and filter low-probability Order Blocks on XAU/USD.",
    category: "Advanced Liquidity",
    author: "XAU Pro Academy Team",
    publishedAt: "2026-04-27",
    updatedAt: "2026-04-27",
    readTime: "7 min",
    coverGradient: "from-gold-muted via-card to-emerald-muted",
    heroImage: inducementTrapCover.url,
    metaDescription:
      "Master Inducement (IDM) in SMC. Learn the golden rule, No IDM means no entry, and filter the highest-probability Order Blocks on XAU/USD.",
    focusKeyword: "Inducement SMC IDM",
    secondaryKeywords: [
      "IDM Trading",
      "First Internal Pullback",
      "Order Block Filter",
      "XAUUSD Fake Breakout",
    ],
    keyTakeaways: [
      "Inducement is the trap Smart Money sets to harvest retail stops before the real move.",
      "The first internal pullback after a BOS is almost always inducement.",
      "Golden rule: No IDM = No Entry.",
      "Filter every Order Block through the 3-question IDM checklist.",
    ],
    toc: [
      { id: "introduction", label: "The Inducement Concept" },
      { id: "first-internal-pullback", label: "The First Internal Pullback" },
      { id: "golden-rule", label: "Golden Rule: No IDM = No Entry" },
      { id: "filtering-order-blocks", label: "Filtering Order Blocks" },
      { id: "xauusd-example", label: "XAU/USD Example" },
      { id: "wrap-up", label: "Wrap-Up" },
      { id: "faq", label: "Frequently Asked Questions" },
    ],
    faq: [
      {
        q: "Is inducement the same as a fake breakout?",
        a: "They're related but distinct. A fake breakout is the visible result; inducement is the mechanism, the small pullback that pulls retail orders in before the sweep. Every fake breakout is built on inducement.",
      },
      {
        q: "How do I tell inducement apart from real structure?",
        a: "Inducement is internal, it sits inside the leg between the last swing point and your Order Block. Real structure is the external swing high or low that defines the trend. Sweeping inducement does not break structure; sweeping real structure does (CHoCH).",
      },
      {
        q: "What if there is no inducement on my Order Block?",
        a: "Skip it. Without IDM, the OB has no fresh liquidity to defend and is much more likely to fail. Wait for price to create an internal pullback, then re-evaluate.",
      },
      {
        q: "Does inducement always get swept before the OB reacts?",
        a: "In high-probability setups, yes, almost always. If price reaches your OB without sweeping the IDM, lower your conviction and reduce size or skip the trade.",
      },
    ],
    relatedSlugs: [
      "liquidity-purge-eqh-eql-gold",
      "high-probability-entry-criteria-gold",
      "smc-gold-trading-strategy",
      "smc-mistakes-prop-firm-challenge",
    ],
    Content: () => import("@/content/articles/inducement-trap-idm-smc").then((m) => ({ default: m.InducementTrapContent })),
  },
  {
    slug: "high-probability-entry-criteria-gold",
    title: "Gold Entry Criteria: 3-Step Checklist",
    excerpt:
      "Combine Structure, Liquidity, and Inducement into one institutional checklist. Learn LTF refinement and how to engineer 1:5+ RR setups on XAU/USD.",
    category: "Advanced Liquidity",
    author: "XAU Pro Academy Team",
    publishedAt: "2026-04-27",
    updatedAt: "2026-04-27",
    readTime: "8 min",
    coverGradient: "from-gold-muted via-card to-emerald-muted",
    metaDescription:
      "The 3-step institutional checklist for every XAU/USD trade. Combine Structure, Liquidity, and Inducement with M1/M5 confirmation for 1:5+ RR.",
    focusKeyword: "High Probability Gold Entry",
    secondaryKeywords: [
      "SMC Entry Checklist",
      "XAUUSD 1:5 RR",
      "LTF Confirmation Entry",
      "Risk Reward Optimization",
    ],
    keyTakeaways: [
      "The perfect setup formula: Structure + Liquidity + Inducement.",
      "Drop to M1/M5 for the CHoCH that gives you a tight, high-RR entry.",
      "Engineer setups for 1:5 RR or better, math beats win rate.",
      "If you can't tick all three boxes out loud, walk away.",
    ],
    toc: [
      { id: "introduction", label: "The Perfect Setup Formula" },
      { id: "checklist", label: "The 3-Step Checklist" },
      { id: "ltf-confirmation", label: "LTF Confirmation (M1/M5)" },
      { id: "risk-reward", label: "Risk/Reward Optimization" },
      { id: "putting-it-together", label: "Putting It All Together" },
      { id: "wrap-up", label: "Wrap-Up" },
      { id: "faq", label: "Frequently Asked Questions" },
    ],
    faq: [
      {
        q: "Do I need all three elements every single time?",
        a: "Yes. Structure gives you direction, Liquidity gives you the catalyst and target, and Inducement validates the entry zone. Missing one drops the setup from A+ to speculation.",
      },
      {
        q: "What's the minimum RR I should accept on Gold?",
        a: "Aim for 1:3 minimum, target 1:5 standard. Below 1:3, transaction costs and slippage erode the edge, especially on Gold where spreads can widen quickly.",
      },
      {
        q: "Is M1 too noisy for entries?",
        a: "Only if you use it without a clear HTF zone. Inside a defined H1/M15 zone, the M1 CHoCH gives surgical entries and cuts your stop dramatically, boosting RR.",
      },
      {
        q: "How many setups like this appear per day on XAU/USD?",
        a: "1–3 A+ setups per day on average across the London and New York sessions. Quality over quantity, one clean 1:5 trade outperforms five sloppy 1:1s.",
      },
    ],
    relatedSlugs: [
      "liquidity-purge-eqh-eql-gold",
      "inducement-trap-idm-smc",
      "smc-gold-trading-strategy",
      "roadmap-2026-gold-trading",
    ],
    Content: () => import("@/content/articles/high-probability-entry-criteria-gold").then((m) => ({ default: m.HighProbabilityEntryContent })),
  },
  {
    slug: "funded-mindset-risk-management",
    title: "Funded Mindset and Risk Management",
    excerpt:
      "Trade $50K–$200K funded accounts like a professional. Master the 1% rule, the buffer strategy, and payout discipline that keep funded traders in the game.",
    category: "Prop Firm Trading",
    author: "XAU Pro Academy Team",
    publishedAt: "2026-04-28",
    updatedAt: "2026-04-28",
    readTime: "6 min",
    coverGradient: "from-emerald-500/20 via-card to-gold-muted",
    metaDescription:
      "Trade funded accounts like a professional. Master the 1% rule, the buffer strategy, and payout discipline that keep funded XAU/USD traders alive.",
    focusKeyword: "Funded Account Risk Management",
    secondaryKeywords: [
      "Prop Firm Mindset",
      "1% Rule Trading",
      "Funded Account Drawdown",
      "Trader Payout Strategy",
    ],
    keyTakeaways: [
      "0.5% per-trade risk is the sweet spot for funded XAU/USD challenges.",
      "Treat 3% as your personal hard stop when the firm allows 5%.",
      "Your first payout is a safety net, not gambling fuel.",
      "Shift from 'how much can I make?' to 'how much can I lose and stay in the game?'",
    ],
    toc: [
      { id: "introduction", label: "A New Relationship With Risk" },
      { id: "one-percent-rule", label: "The 1% Rule" },
      { id: "buffer-strategy", label: "The Buffer Strategy" },
      { id: "payout-discipline", label: "Payout Discipline" },
      { id: "conclusion", label: "From Hunter to Manager" },
      { id: "faq", label: "Frequently Asked Questions" },
    ],
    faq: [
      {
        q: "Why is 0.5% better than 1% per trade on a funded account?",
        a: "At 0.5% you can absorb six straight losses and still sit inside a 3% drawdown buffer. At 1%, three losses already puts you in panic mode, and panic causes violations.",
      },
      {
        q: "What is the 'buffer strategy' for funded accounts?",
        a: "It means setting a personal daily loss limit stricter than the firm's. If the firm allows 5%, you stop at 3%. The 2% buffer absorbs spread spikes, news whipsaws, and bad fills on Gold.",
      },
      {
        q: "Should I withdraw my first payout or reinvest it?",
        a: "Withdraw it. Use it for living expenses, an emergency fund, or to seed a backup challenge at a different firm. Trading from a place of need destroys decision quality.",
      },
      {
        q: "How many trades per day should I take on a funded challenge?",
        a: "Two or three high-quality setups maximum. The number-one cause of failed challenges is overtrading, taking 8 trades on a day that only offered 1 valid setup.",
      },
      {
        q: "Can I scale up risk after passing the evaluation?",
        a: "Yes, but only after 30+ consistent trades on the funded phase at reduced risk. Even then, increase gradually (0.5% → 0.75% → 1%) and never above 1% per trade.",
      },
    ],
    relatedSlugs: [
      "smc-execution-checklist-prop-firm",
      "drawdown-recovery-strategy",
      "smc-mistakes-prop-firm-challenge",
      "drawdown-psychology-funded-accounts",
    ],
    Content: () => import("@/content/articles/funded-mindset-risk-management").then((m) => ({ default: m.FundedMindsetRiskManagementContent })),
  },
  {
    slug: "smc-execution-checklist-prop-firm",
    title: "SMC Execution Checklist for Prop Firms",
    excerpt:
      "Trade like a sniper, not a machine gunner. The four-step SMC checklist that filters every Gold trade, HTF bias, liquidity sweep, MSS, and FVG/OB entry.",
    category: "Strategy",
    author: "XAU Pro Academy Team",
    publishedAt: "2026-04-28",
    updatedAt: "2026-04-28",
    readTime: "7 min",
    coverGradient: "from-emerald-500/20 via-card to-gold-muted",
    metaDescription:
      "The four-step SMC execution checklist for funded XAU/USD traders: HTF bias, liquidity sweep, market structure shift, FVG/OB entry. No step, no trade.",
    focusKeyword: "SMC Execution Checklist",
    secondaryKeywords: [
      "Prop Firm Entry Strategy",
      "XAUUSD Sniper Entry",
      "Market Structure Shift Gold",
      "FVG Order Block Entry",
    ],
    keyTakeaways: [
      "Every funded trade needs four conditions aligned: HTF bias, liquidity sweep, MSS, and FVG/OB entry.",
      "If one step is missing, there is no trade, patience is the most profitable setup.",
      "Real MSS is a strong displacement candle, not a one-pip break.",
      "Run the checklist out loud before every click on XAU/USD.",
    ],
    toc: [
      { id: "introduction", label: "Sniper, Not Machine Gunner" },
      { id: "step-1-htf-bias", label: "Step 1, HTF Bias" },
      { id: "step-2-liquidity-hunt", label: "Step 2, Liquidity Hunt" },
      { id: "step-3-mss", label: "Step 3, Market Structure Shift" },
      { id: "step-4-entry", label: "Step 4, The Entry" },
      { id: "execution-rule", label: "The Execution Rule" },
      { id: "conclusion", label: "The Checklist Is Your Edge" },
      { id: "faq", label: "Frequently Asked Questions" },
    ],
    faq: [
      {
        q: "What if 3 of 4 conditions align, can I still take the trade?",
        a: "No. The whole point of the checklist is binary execution. Three out of four is a 'B-grade' setup, and B-grade setups are exactly what blow funded accounts.",
      },
      {
        q: "Which timeframe is the entry executed on?",
        a: "Bias is set on the Daily and 4H. Liquidity sweep and MSS are confirmed on the 5m or 1m. The actual entry is at the FVG or refined Order Block created by the displacement that caused the MSS.",
      },
      {
        q: "How do I know a sweep is real and not just a wick?",
        a: "A real sweep is followed by a Market Structure Shift in the opposite direction. No MSS means no reversal, just price grabbing wick liquidity before continuing the original move.",
      },
      {
        q: "What if no setup appears all day?",
        a: "Then you don't trade. Most days only offer 0–2 valid setups on Gold. A 'no-trade' day is a winning day on a funded account.",
      },
      {
        q: "How tight should the stop loss be?",
        a: "Place the stop just beyond the structural swing that created your entry, typically 15–25 pips on Gold. Tighter than that and the spread plus a normal liquidity wick will take you out.",
      },
    ],
    relatedSlugs: [
      "funded-mindset-risk-management",
      "drawdown-recovery-strategy",
      "high-probability-entry-criteria-gold",
      "amd-pattern-gold-daily-cycles",
    ],
    Content: () => import("@/content/articles/smc-execution-checklist-prop-firm").then((m) => ({ default: m.SmcExecutionChecklistPropFirmContent })),
  },
  {
    slug: "drawdown-recovery-strategy",
    title: "Drawdown and Recovery: Tactical Playbook",
    excerpt:
      "A three-rule recovery playbook for funded traders in drawdown: walk away after two losses, cut size in half, and reset your relationship with PnL.",
    category: "Psychology",
    author: "XAU Pro Academy Team",
    publishedAt: "2026-04-28",
    updatedAt: "2026-04-28",
    readTime: "6 min",
    coverGradient: "from-emerald-500/20 via-card to-gold-muted",
    metaDescription:
      "A three-rule tactical playbook for surviving drawdown on a funded account: the walk-away rule, cutting size in half, and the PnL detachment reset.",
    focusKeyword: "Drawdown Recovery Strategy",
    secondaryKeywords: [
      "Revenge Trading Prevention",
      "Funded Account Drawdown",
      "Trading Discipline Recovery",
      "Reduce Risk in Drawdown",
    ],
    keyTakeaways: [
      "Two consecutive losses = platform off, walk away for at least one hour.",
      "In a 2–3% drawdown, cut per-trade risk in half (0.5% → 0.25%).",
      "Detach identity from PnL, you are a manager of probability, not a fortune teller.",
      "Discipline is doing what needs to be done, even when you don't feel like it.",
    ],
    toc: [
      { id: "introduction", label: "Drawdown Is Not a Failure" },
      { id: "walk-away", label: "Rule 1, The Walk Away Rule" },
      { id: "reduce-size", label: "Rule 2, Reduce Your Size" },
      { id: "mental-reset", label: "Rule 3, The Mental Reset" },
      { id: "discipline-definition", label: "The Real Definition of Discipline" },
      { id: "conclusion", label: "Recovery Is a Skill" },
      { id: "faq", label: "Frequently Asked Questions" },
    ],
    faq: [
      {
        q: "Why two losses and not three before walking away?",
        a: "Statistical research on retail trader behavior shows the third trade after two losses is almost always emotional, oversized, and outside the plan. Cutting it off at two prevents the most expensive click of the week.",
      },
      {
        q: "Should I really cut my risk in half during drawdown?",
        a: "Yes. The goal in drawdown is not to recover the loss in one trade, it's to rebuild rhythm and confidence through small, well-executed setups. Three clean wins at 0.25% does more for your account than one revenge trade at 1%.",
      },
      {
        q: "How long should I stay away from the charts after two losses?",
        a: "Minimum one hour for a session reset. After a 3%+ drawdown day, take the rest of the day off entirely. Sleep on it. The market will be there tomorrow.",
      },
      {
        q: "How do I stop tying my self-worth to my PnL?",
        a: "Review your journal asking only one question: 'Did I follow my checklist?' If yes, the loss was the cost of doing business. This separates identity (process) from outcome (PnL variance).",
      },
      {
        q: "What if I keep breaking these rules?",
        a: "Pre-commit them. Set a hard rule with your platform: 2 losses = MT5 closed for the day. Use accountability, share your daily P&L with a trading partner. Discipline is built through pre-commitment, not in-the-moment willpower.",
      },
    ],
    relatedSlugs: [
      "funded-mindset-risk-management",
      "smc-execution-checklist-prop-firm",
      "drawdown-psychology-funded-accounts",
      "smc-mistakes-prop-firm-challenge",
    ],
    Content: () => import("@/content/articles/drawdown-recovery-strategy").then((m) => ({ default: m.DrawdownRecoveryStrategyContent })),
  },
  {
    slug: "what-is-smart-money-concepts",
    title: "What Are Smart Money Concepts (SMC)? A Beginner's Guide",
    seoTitle: "What Are Smart Money Concepts (SMC)?",
    excerpt:
      "A clear, institutional definition of Smart Money Concepts. Learn the three pillars (market structure, liquidity, points of interest) and how SMC differs from retail analysis.",
    category: "SMC Foundations",
    author: "XAU Pro Academy Team",
    publishedAt: "2026-05-15",
    updatedAt: "2026-10-22",
    readTime: "7 min",
    coverGradient: "from-gold-muted via-card to-slate-deep",
    metaDescription:
      "Smart Money Concepts in plain terms: how institutions use liquidity, market structure and order blocks, why retail breakouts fail, and how to learn SMC in order.",
    focusKeyword: "what is smart money concepts",
    secondaryKeywords: [
      "smart money concepts",
      "smc trading",
      "smc definition",
      "smart money concepts explained",
    ],
    keyTakeaways: [
      "SMC models how institutions accumulate and distribute positions through liquidity, not indicators.",
      "The three pillars: Market Structure (BOS/CHoCH), Liquidity (BSL/SSL/IDM), and Points of Interest (Order Blocks/FVGs).",
      "SMC inverts retail logic: breakouts are usually fills, support/resistance levels are usually liquidity targets.",
      "Learn it sequentially: structure first, then liquidity, then POIs, then session timing.",
    ],
    toc: [
      { id: "introduction", label: "What SMC Actually Is" },
      { id: "three-pillars", label: "The Three Pillars" },
      { id: "how-it-differs", label: "How SMC Differs From Retail" },
      { id: "how-to-learn", label: "How to Learn SMC" },
      { id: "wrap-up", label: "Wrap-Up" },
      { id: "faq", label: "Frequently Asked Questions" },
    ],
    faq: [
      {
        q: "What does SMC stand for?",
        a: "SMC stands for Smart Money Concepts. It is a price action methodology that models how institutional participants move markets through liquidity and structural intent rather than indicators.",
      },
      {
        q: "Is Smart Money Concepts the same as ICT?",
        a: "They are siblings, not identical. ICT (Inner Circle Trader) is the original encyclopedic methodology by Michael Huddleston. SMC is a streamlined distillation that keeps the core pillars and drops most of the complex pattern naming. SMC is the easier starting point.",
      },
      {
        q: "Do I need an indicator to trade SMC?",
        a: "No. SMC is purely structural. The only tools required are a clean chart and the ability to mark swing highs, swing lows, equal highs, equal lows, order blocks, and fair value gaps.",
      },
      {
        q: "Is SMC suitable for beginners?",
        a: "Yes, but discipline is the bottleneck, not concept. The framework is simple enough for a beginner to learn in a few weeks. Executing it without overtrading takes months of journaled practice.",
      },
    ],
    relatedSlugs: [
      "order-blocks-fvg-explained",
      "ict-vs-smc-explained",
      "smc-gold-trading-strategy",
      "inducement-trap-idm-smc",
    ],
    Content: () => import("@/content/articles/what-is-smart-money-concepts").then((m) => ({ default: m.WhatIsSmartMoneyConceptsContent })),
  },
  {
    slug: "order-blocks-fvg-explained",
    title: "Order Blocks and Fair Value Gaps Explained",
    seoTitle: "Order Blocks and Fair Value Gaps",
    excerpt:
      "The core SMC entry mechanic. Learn how Order Blocks and Fair Value Gaps work together, and the three conditions that separate A+ setups from B-grade noise.",
    category: "SMC Foundations",
    author: "XAU Pro Academy Team",
    publishedAt: "2026-05-15",
    updatedAt: "2026-05-15",
    readTime: "8 min",
    coverGradient: "from-emerald-muted via-card to-gold-muted",
    metaDescription:
      "Master Order Blocks and Fair Value Gaps (FVG), the core SMC entry mechanic. Three conditions that qualify a high probability institutional zone.",
    focusKeyword: "order block fair value gap",
    secondaryKeywords: [
      "order block",
      "fair value gap",
      "fvg trading",
      "smc point of interest",
      "poi trading",
    ],
    keyTakeaways: [
      "An Order Block is the last opposite-direction candle before a strong impulsive BOS.",
      "A Fair Value Gap is a three-candle inefficiency where wicks 1 and 3 do not overlap.",
      "A high probability OB requires liquidity sweep, FVG displacement, and a clean BOS, all three.",
      "The A+ setup is an FVG sitting inside or adjacent to an Order Block at a swept liquidity zone.",
    ],
    toc: [
      { id: "introduction", label: "The Core SMC Mechanic" },
      { id: "order-blocks", label: "What Is an Order Block" },
      { id: "fair-value-gaps", label: "What Is a Fair Value Gap" },
      { id: "ob-plus-fvg", label: "The A+ Setup: OB + FVG" },
      { id: "poi-confluence", label: "Stacking POI Confluence" },
      { id: "wrap-up", label: "Wrap-Up" },
      { id: "faq", label: "Frequently Asked Questions" },
    ],
    faq: [
      {
        q: "What is an Order Block in trading?",
        a: "An Order Block is the last opposite-direction candle before a strong impulsive move that breaks structure. It marks where institutions filled their positions before driving the market.",
      },
      {
        q: "What is a Fair Value Gap (FVG)?",
        a: "An FVG is a three-candle pattern where the wick of candle 1 and the wick of candle 3 do not overlap, leaving a price range that was not traded efficiently. Price has a strong tendency to return and rebalance an FVG.",
      },
      {
        q: "Should I trade Order Blocks or FVGs?",
        a: "Both. The cleanest entries occur where an FVG sits inside or adjacent to an Order Block. The OB defines the institutional zone. The FVG offers a precise re-entry trigger.",
      },
      {
        q: "How do I know an Order Block is high probability?",
        a: "It must satisfy three conditions: it swept liquidity immediately before the impulse, the impulse out of it created an FVG, and the impulse caused a clean Break of Structure. If any condition is missing, skip it.",
      },
    ],
    relatedSlugs: [
      "what-is-smart-money-concepts",
      "inducement-trap-idm-smc",
      "high-probability-entry-criteria-gold",
      "smc-gold-trading-strategy",
    ],
    Content: () => import("@/content/articles/order-blocks-fvg-explained").then((m) => ({ default: m.OrderBlocksFvgExplainedContent })),
  },
  {
    slug: "ict-vs-smc-explained",
    title: "ICT vs SMC: What's the Difference and Which Should You Learn?",
    seoTitle: "ICT vs SMC: Key Differences Explained",
    excerpt:
      "ICT and SMC are siblings, not rivals. Understand the lineage, the key practical differences, and why SMC is the better starting point for most traders today.",
    category: "SMC Foundations",
    author: "XAU Pro Academy Team",
    publishedAt: "2026-05-15",
    updatedAt: "2026-05-15",
    readTime: "6 min",
    coverGradient: "from-slate-deep via-card to-gold-muted",
    metaDescription:
      "ICT vs SMC explained. The lineage, the practical differences, and why Smart Money Concepts is the better starting point for traders learning institutional order flow.",
    focusKeyword: "ict vs smc",
    secondaryKeywords: [
      "ict concepts",
      "ict trading",
      "ict meaning trading",
      "smart money concepts vs ict",
    ],
    keyTakeaways: [
      "ICT is the original, encyclopedic methodology by Michael Huddleston. SMC is a streamlined distillation.",
      "Same institutional logic, different vocabulary and complexity. SMC drops most pattern-specific naming.",
      "For most traders, SMC is the better starting point. ICT becomes a reference library afterward.",
      "Pick one framework and finish it. Toggling methodologies is the fastest way to fail.",
    ],
    toc: [
      { id: "introduction", label: "ICT vs SMC Overview" },
      { id: "ict-origin", label: "Where ICT Came From" },
      { id: "smc-origin", label: "Where SMC Came From" },
      { id: "key-differences", label: "Key Differences in Practice" },
      { id: "which-should-you-learn", label: "Which Should You Learn" },
      { id: "how-xau-pro-applies-it", label: "How XAU Pro Applies SMC" },
      { id: "wrap-up", label: "Wrap-Up" },
      { id: "faq", label: "Frequently Asked Questions" },
    ],
    faq: [
      {
        q: "Is ICT the same as SMC?",
        a: "No, but they are closely related. ICT is the original methodology by Michael Huddleston, dense and comprehensive. SMC is a streamlined version that keeps the institutional pillars (liquidity, structure, order blocks, FVGs) and drops most of the encyclopedic complexity.",
      },
      {
        q: "Should I learn ICT or SMC first?",
        a: "Learn SMC first. It teaches the same institutional logic with a fraction of the cognitive load. Once you can execute clean SMC on live charts, ICT material becomes a useful reference library to deepen specific concepts.",
      },
      {
        q: "What does ICT stand for in trading?",
        a: "ICT stands for Inner Circle Trader, the brand of Michael Huddleston whose work pioneered modern institutional price action methodology.",
      },
      {
        q: "Can I mix ICT and SMC?",
        a: "Once you are competent in one, yes. While learning, no. Mixing two frameworks before mastering either is the most common reason traders never become consistent. Pick one, ship trades, then borrow concepts later.",
      },
    ],
    relatedSlugs: [
      "what-is-smart-money-concepts",
      "order-blocks-fvg-explained",
      "smc-gold-trading-strategy",
    ],
    Content: () => import("@/content/articles/ict-vs-smc-explained").then((m) => ({ default: m.IctVsSmcExplainedContent })),
  },
  {
    slug: "fair-value-gap-xauusd-guide",
    title: "Fair Value Gap on XAU/USD: How to Spot and Trade FVGs",
    seoTitle: "Fair Value Gap on Gold (XAUUSD): Which FVGs Hold",
    excerpt:
      "Learn how to spot, validate, and trade Fair Value Gaps on gold. A four box checklist and the M15 execution flow used by institutional traders.",
    category: "SMC Foundations",
    author: "XAU Pro Academy Team",
    publishedAt: "2026-05-19",
    updatedAt: "2026-10-22",
    readTime: "10 min",
    heroImage: fairValueGapDiagramImage,
    video: {
      youtubeId: "PQiI2TmVhBY",
      name: "Why 80% of Fair Value Gaps Fail on Gold",
      description:
        "An FVG is not support or resistance. Three institutional checks before trading any gold Fair Value Gap: prior liquidity sweep, confirmed body close, and premium versus discount location.",
      uploadDate: "2026-11-03T00:00:00Z",
      duration: "PT1M6S",
    },
    coverGradient: "from-gold-muted via-card to-emerald-muted",
    metaDescription:
      "Most gold FVGs fail. See which XAUUSD gaps price actually respects: 4 checks, the M15 entry at the 50% level, and real chart examples. Free guide.",
    focusKeyword: "Fair Value Gap",
    secondaryKeywords: [
      "FVG XAUUSD",
      "Fair Value Gap gold",
      "Imbalance trading",
      "SMC FVG strategy",
    ],
    keyTakeaways: [
      "A valid FVG needs three candles with no wick overlap between candle 1 and candle 3.",
      "Institutional FVGs always follow a liquidity sweep and a break of structure.",
      "The 50 percent (Consequent Encroachment) level balances entry price against invalidation.",
      "FVGs against H4 bias are continuation traps, not reversal trades.",
    ],
    toc: [
      { id: "introduction", label: "What a Fair Value Gap Really Is" },
      { id: "how-to-spot", label: "How to Spot a Valid FVG on Gold" },
      { id: "fvg-quality", label: "Which FVGs Deserve Attention" },
      { id: "trading-fvg", label: "Trading the FVG on XAU/USD" },
      { id: "worked-example", label: "Worked Example: A Bullish FVG on M15" },
      { id: "common-mistakes", label: "Common Mistakes That Kill the Edge" },
      { id: "wrap-up", label: "Wrap-Up" },
      { id: "faq", label: "Frequently Asked Questions" },
    ],
    faq: [
      {
        q: "What is a Fair Value Gap in trading?",
        a: "A Fair Value Gap (FVG) is a three candle imbalance where the wicks of candle 1 and candle 3 do not overlap, leaving an unfilled zone. It represents a price inefficiency that institutions typically return to rebalance.",
      },
      {
        q: "How do you trade a Fair Value Gap on gold?",
        a: "Set H4 bias, wait for an M15 sweep of liquidity, identify the displacement candle that created the FVG, and place a limit order at the 50 percent of the gap. Stop loss goes beyond the swept wick, targets at the next liquidity pool.",
      },
      {
        q: "Do Fair Value Gaps always get filled?",
        a: "Most FVGs get at least partially filled, but not all. FVGs that form after a clean liquidity sweep and a break of structure are the most reliable. FVGs without a prior sweep are far less reliable.",
      },
      {
        q: "What timeframe is best for FVGs on XAU/USD?",
        a: "M15 is the preferred timeframe for execution on gold. H4 sets the bias, M15 provides the entry. Lower timeframes (M1 to M5) generate too many false signals.",
      },
      {
        q: "What is the difference between an FVG and a regular gap?",
        a: "A regular gap forms between trading sessions when price opens away from the prior close. An FVG forms inside a continuous trading session as a three candle imbalance. FVGs are the institutional footprint.",
      },
    ],
    relatedSlugs: [
      "order-blocks-fvg-explained",
      "liquidity-sweep-gold-trading",
      "what-is-smart-money-concepts",
    ],
    Content: () => import("@/content/articles/fair-value-gap-xauusd-guide").then((m) => ({ default: m.FairValueGapXauusdGuideContent })),
  },
  {
    slug: "liquidity-sweep-gold-trading",
    title: "Liquidity Sweep Trading on Gold: The Complete Setup",
    seoTitle: "Liquidity Sweep Trading on Gold",
    excerpt:
      "How institutions sweep stop pools on XAU/USD and how to trade the reversal. The four liquidity pools that matter and the repeatable execution flow.",
    category: "SMC Foundations",
    author: "XAU Pro Academy Team",
    publishedAt: "2026-05-19",
    updatedAt: "2026-05-19",
    readTime: "8 min",
    coverGradient: "from-gold-muted via-card to-emerald-muted",
    metaDescription:
      "Liquidity sweep trading on gold. The 4 pools that matter, how to tell a sweep from a break of structure, and the M15 execution flow for XAU/USD.",
    focusKeyword: "Liquidity Sweep",
    secondaryKeywords: [
      "Liquidity sweep gold",
      "Stop hunt XAUUSD",
      "Smart money liquidity",
      "Judas Swing gold",
    ],
    keyTakeaways: [
      "A sweep is wick beyond a level with the close back inside. A close beyond is a break of structure.",
      "PDH, PDL, Asia high/low, and equal highs/lows are the four highest probability pools on XAU/USD.",
      "Wait for a Change of Character after the sweep before entering. Anticipating the sweep is the biggest cause of getting wicked out.",
      "Gold often double sweeps, so stop loss must sit above the highest wick plus a small buffer.",
    ],
    toc: [
      { id: "introduction", label: "Why Liquidity Sweeps Drive Every Gold Move" },
      { id: "types-of-liquidity", label: "The Four Pools That Matter on XAU/USD" },
      { id: "sweep-vs-bos", label: "Liquidity Sweep vs Break of Structure" },
      { id: "trading-the-sweep", label: "A Repeatable Sweep Setup" },
      { id: "why-it-fails", label: "Why Most Traders Fail With Sweeps" },
      { id: "faq", label: "Frequently Asked Questions" },
    ],
    faq: [
      {
        q: "What is a liquidity sweep in trading?",
        a: "A liquidity sweep is when price spikes through a visible pool of stop orders (above a swing high or below a swing low), trips them, then reverses. It is the mechanism institutions use to fill large orders without moving the market against themselves.",
      },
      {
        q: "How do I know if it is a sweep or a real breakout?",
        a: "Look at the candle close. If the candle wick goes beyond the level but the body closes back inside the prior range, it is a sweep. If the body closes beyond the level, it is a break of structure and signals continuation.",
      },
      {
        q: "What is the Judas Swing on gold?",
        a: "The Judas Swing is a classic liquidity sweep at the London open where price spikes through Asia session high or low to trip stops, then reverses to set the real direction for the trading day. It plays out on XAU/USD on roughly 60 to 70 percent of sessions.",
      },
      {
        q: "Where do I put my stop loss on a sweep trade?",
        a: "Above the highest sweep wick (for shorts) or below the lowest sweep wick (for longs), plus a 5 to 10 pip buffer. Gold frequently double sweeps, so tight stops on the wick itself get wicked out.",
      },
      {
        q: "What is the best timeframe for trading liquidity sweeps?",
        a: "M15 is the cleanest execution timeframe on XAU/USD. Mark liquidity pools using H4 swings, the previous day, and the Asia session, then execute on the M15 sweep and Change of Character.",
      },
    ],
    relatedSlugs: [
      "liquidity-purge-eqh-eql-gold",
      "what-is-smart-money-concepts",
      "fair-value-gap-xauusd-guide",
    ],
    Content: () => import("@/content/articles/liquidity-sweep-gold-trading").then((m) => ({ default: m.LiquiditySweepGoldTradingContent })),
  },
  {
    slug: "smc-trading-strategy-gold",
    title: "SMC Trading Strategy for Gold: A Step by Step Guide",
    seoTitle: "Does SMC Work on Gold? 6 Step XAUUSD Strategy",
    excerpt:
      "The complete SMC trading workflow for XAU/USD. Three pillars, six step execution flow, and the risk rules that keep funded accounts alive.",
    category: "SMC Foundations",
    author: "XAU Pro Academy Team",
    publishedAt: "2026-05-19",
    updatedAt: "2026-10-12",
    readTime: "9 min",
    coverGradient: "from-gold-muted via-card to-emerald-muted",
    metaDescription:
      "Does SMC work on gold? The 6 step XAUUSD workflow: H4 bias, liquidity sweep, displacement, POI entry and fixed risk, backed by an audited trade record.",
    focusKeyword: "SMC Trading",
    secondaryKeywords: [
      "SMC trading strategy",
      "Smart Money Concepts gold",
      "SMC XAUUSD",
      "Institutional trading framework",
    ],
    keyTakeaways: [
      "SMC trading rests on three pillars: market structure, liquidity, and points of interest.",
      "The high probability stack is always sweep, then CHoCH, then POI entry.",
      "H4 sets bias, M15 sets execution. Never trade M15 against H4.",
      "Risk capped at 1 percent per trade, 0.5 percent on prop firm challenges.",
    ],
    toc: [
      { id: "introduction", label: "SMC Trading: The Institutional Framework for Gold" },
      { id: "three-pillars", label: "The Three Pillars of the SMC Strategy" },
      { id: "workflow", label: "The XAU Pro SMC Workflow" },
      { id: "risk", label: "Risk Rules That Keep You Funded" },
      { id: "why-smc-works", label: "Why SMC Works on Gold Specifically" },
      { id: "wrap-up", label: "Wrap-Up" },
      { id: "faq", label: "Frequently Asked Questions" },
    ],
    faq: [
      {
        q: "What is SMC trading?",
        a: "SMC (Smart Money Concepts) is a price action framework that reads charts the way institutions execute orders. It replaces indicators with three measurable inputs: market structure, liquidity, and points of interest such as order blocks and Fair Value Gaps.",
      },
      {
        q: "Is SMC trading profitable on gold?",
        a: "Gold has high participation, concentrated session liquidity, and sharp structural reactions. SMC produces some of the cleanest setups in any market on XAU/USD, but profitability depends entirely on discipline and risk control, not on the strategy itself.",
      },
      {
        q: "How long does it take to learn SMC trading?",
        a: "Expect 3 to 6 months of focused study and journaled trades to build pattern recognition. The concepts are simple. The execution discipline is what takes time.",
      },
      {
        q: "What is the best timeframe for SMC on XAU/USD?",
        a: "H4 for bias, M15 for execution. Lower timeframes increase noise and emotional decisions. Higher timeframes (Daily, Weekly) are useful for swing trading but slower to produce setups.",
      },
      {
        q: "Can I use SMC on a prop firm challenge?",
        a: "Yes, and SMC is well suited because it produces clearly defined risk, reward, and invalidation on every setup. Cap risk at 0.5 percent per trade, limit yourself to two losses per day, and never trade through high impact news.",
      },
    ],
    relatedSlugs: [
      "what-is-smart-money-concepts",
      "fair-value-gap-xauusd-guide",
      "liquidity-sweep-gold-trading",
      "ict-vs-smc-explained",
    ],
    Content: () => import("@/content/articles/smc-trading-strategy-gold").then((m) => ({ default: m.SmcTradingStrategyGoldContent })),
  },
  {
    slug: "ict-displacement-explained",
    title: "ICT Displacement Explained: Reading Institutional Intent",
    seoTitle: "ICT Displacement Explained",
    excerpt:
      "Displacement is the proof of institutional intent on the chart. How to spot a real displacement on XAU/USD and the setup that follows it.",
    category: "SMC Foundations",
    author: "XAU Pro Academy Team",
    publishedAt: "2026-05-19",
    updatedAt: "2026-11-03",
    readTime: "7 min",
    video: {
      youtubeId: "yffGWax_OOY",
      name: "Wick vs Body Close: The Gold Displacement Rule",
      description:
        "A wick above a key high on gold is a liquidity raid, not a break of structure. Three checks for a valid break: body close beyond the level, displacement, and follow through.",
      uploadDate: "2026-11-03T00:00:00Z",
      duration: "PT1M5S",
    },
    coverGradient: "from-gold-muted via-card to-emerald-muted",
    metaDescription:
      "ICT displacement explained. The 3 conditions for a valid displacement, why it matters for execution, and a repeatable setup for XAU/USD.",
    focusKeyword: "ICT Displacement",
    secondaryKeywords: [
      "Displacement forex",
      "ICT trading",
      "Displacement candle",
      "Institutional displacement",
    ],
    keyTakeaways: [
      "Displacement is a body driven impulsive move that breaks structure and leaves a Fair Value Gap.",
      "A wick through a level is a sweep, not a displacement.",
      "Displacement after a liquidity sweep is the highest probability institutional footprint.",
      "Entry is on the retracement into the FVG or order block, never on the displacement candle itself.",
    ],
    toc: [
      { id: "introduction", label: "What ICT Displacement Means" },
      { id: "how-to-identify", label: "How to Identify a Real Displacement" },
      { id: "why-it-matters", label: "Why Displacement Matters for Execution" },
      { id: "trading-displacement", label: "A Repeatable Displacement Setup on Gold" },
      { id: "common-errors", label: "Common Errors Around Displacement" },
      { id: "wrap-up", label: "Wrap-Up" },
      { id: "faq", label: "Frequently Asked Questions" },
    ],
    faq: [
      {
        q: "What is displacement in ICT trading?",
        a: "Displacement is a sharp, body driven move that proves institutional intent. It must close beyond a prior swing point (breaking structure) and leave a Fair Value Gap behind it. It is the signal that institutions have stopped accumulating and started delivering.",
      },
      {
        q: "How do you spot a displacement candle?",
        a: "Three conditions: the body is at least twice the size of the average body from the prior 10 candles, the candle closes beyond a prior swing high or low, and the surrounding candles leave a Fair Value Gap.",
      },
      {
        q: "What is the difference between displacement and a liquidity sweep?",
        a: "A sweep takes out stops with a wick and closes back inside the prior range. Displacement closes beyond the level with a strong body. The cleanest setup is a sweep followed by displacement in the opposite direction.",
      },
      {
        q: "Should I enter on the displacement candle?",
        a: "No. Entry is on the retracement into the order block or Fair Value Gap created by the displacement. Chasing the displacement candle leads to poor risk to reward and frequent stop outs.",
      },
      {
        q: "When does displacement happen on XAU/USD?",
        a: "Displacement on gold prints most often during the London open (07:00 to 09:00 GMT) and the New York open (12:30 to 14:30 GMT). These are the windows where institutional volume drives the largest impulsive moves.",
      },
    ],
    relatedSlugs: [
      "fair-value-gap-xauusd-guide",
      "ict-vs-smc-explained",
      "order-blocks-fvg-explained",
    ],
    Content: () => import("@/content/articles/ict-displacement-explained").then((m) => ({ default: m.IctDisplacementExplainedContent })),
  },
  {
    slug: "institutional-desk-routine-gold",
    title: "The Institutional Desk Routine for Gold (XAUUSD): London, New York and Liquidity",
    seoTitle: "Institutional Desk Routine for Gold (XAUUSD)",
    excerpt:
      "A fixed, session by session routine for gold: macro check, H4 bias, liquidity map before London, confirmation rules for London and New York, and risk filters before any order.",
    category: "Execution",
    author: "XAU Pro Academy Team",
    publishedAt: "2026-10-16",
    updatedAt: "2026-10-16",
    readTime: "9 min",
    coverGradient: "from-gold-muted via-card to-emerald-muted",
    metaDescription:
      "The institutional desk routine for gold (XAUUSD): calendar check, H4 bias, liquidity mapping before London, sweep confirmation, New York overlap rules and risk filters.",
    focusKeyword: "XAUUSD trading routine",
    secondaryKeywords: [
      "gold trading routine",
      "London session gold",
      "New York session gold",
      "XAUUSD liquidity sweep",
      "institutional gold trading",
    ],
    keyTakeaways: [
      "Decide whether the day is tradable before looking for entries. Neutral is a valid conclusion.",
      "Bias comes from D1 and H4 structure and is written down before London opens.",
      "Map the Asia range, PDH, PDL, equal highs and lows and open POIs before the session.",
      "Never enter on the sweep candle. Wait for displacement and an M15 structure shift.",
      "Stops are structural and volatility aware, with a fixed daily loss cap.",
    ],
    toc: [
      { id: "introduction", label: "Why a Desk Routine Matters" },
      { id: "phase-1-macro", label: "Phase 1: Macro and Calendar Check" },
      { id: "phase-2-htf", label: "Phase 2: Higher Timeframe Bias" },
      { id: "phase-3-liquidity-map", label: "Phase 3: Mapping Liquidity Before London" },
      { id: "phase-4-london", label: "Phase 4: The London Session Read" },
      { id: "phase-5-new-york", label: "Phase 5: New York and the Overlap" },
      { id: "phase-6-risk", label: "Phase 6: Risk Rules Before Any Order" },
      { id: "checklist", label: "The Complete Desk Checklist" },
      { id: "common-mistakes", label: "Common Mistakes" },
      { id: "faq", label: "Frequently Asked Questions" },
    ],
    faq: [
      {
        q: "What is the best session to trade gold (XAUUSD)?",
        a: "The London open and the London and New York overlap (roughly 07:00 to 16:00 UTC) carry the deepest liquidity. The Asia session is thinner and produces more false structure.",
      },
      {
        q: "How long should a pre-session gold routine take?",
        a: "A disciplined routine usually takes 15 to 30 minutes: calendar check, D1 and H4 bias, and a liquidity map of the Asia range, prior day extremes and open points of interest.",
      },
      {
        q: "Should I enter when gold sweeps the Asia high or low?",
        a: "Not on the sweep itself. A valid entry needs displacement after the sweep and an M15 structure shift aligned with H4 bias, followed by a retrace into the zone that created the shift.",
      },
      {
        q: "How do news releases fit into the routine?",
        a: "High impact releases such as CPI, NFP and FOMC are marked as no-entry windows. The first spike is treated as information. Structure after the release decides the plan.",
      },
      {
        q: "When should a trader stop for the day?",
        a: "When the daily loss cap is reached or after two consecutive stops in the same session. Pausing protects capital and prevents reactive re-entries.",
      },
    ],
    relatedSlugs: [
      "liquidity-sweep-gold-trading",
      "fair-value-gap-xauusd-guide",
      "smc-gold-trading-strategy",
    ],
    Content: () =>
      import("@/content/articles/institutional-desk-routine-gold").then((m) => ({
        default: m.InstitutionalDeskRoutineGoldContent,
      })),
  },
  {
    slug: "choch-vs-bos-smart-money",
    title: "CHoCH vs BOS: The Two Signals That Define Trend in SMC",
    seoTitle: "CHoCH vs BOS in Smart Money Concepts",
    excerpt:
      "Change of Character and Break of Structure are the two events that define every SMC trade. How to read each one correctly on XAU/USD.",
    category: "SMC Foundations",
    author: "XAU Pro Academy Team",
    publishedAt: "2026-05-19",
    updatedAt: "2026-05-19",
    readTime: "7 min",
    coverGradient: "from-gold-muted via-card to-emerald-muted",
    metaDescription:
      "CHoCH vs BOS explained. The difference between Change of Character and Break of Structure, and how to use both to read XAU/USD trend correctly.",
    focusKeyword: "CHoCH vs BOS",
    secondaryKeywords: [
      "Change of Character",
      "Break of Structure",
      "SMC structure",
      "Market structure trading",
    ],
    keyTakeaways: [
      "BOS confirms the existing trend. CHoCH signals it is finished.",
      "Both events require a body close beyond the level, not just a wick.",
      "The highest probability setup is sweep, then CHoCH, then entry at the order block that produced the CHoCH.",
      "Counter trend CHoCH inside a strong H4 trend usually fails. Align M15 CHoCH with H4 bias.",
    ],
    toc: [
      { id: "introduction", label: "CHoCH vs BOS: The Two Signals That Define Trend" },
      { id: "break-of-structure", label: "Break of Structure (BOS)" },
      { id: "change-of-character", label: "Change of Character (CHoCH)" },
      { id: "side-by-side", label: "Side by Side: CHoCH vs BOS" },
      { id: "on-xauusd", label: "Applying CHoCH and BOS on XAU/USD" },
      { id: "common-mistakes", label: "Common Mistakes Reading Structure" },
      { id: "faq", label: "Frequently Asked Questions" },
    ],
    faq: [
      {
        q: "What is the difference between CHoCH and BOS?",
        a: "BOS (Break of Structure) is a continuation signal that closes beyond a swing point in the direction of the existing trend. CHoCH (Change of Character) is a reversal signal that closes beyond a swing point against the existing trend.",
      },
      {
        q: "Does a wick count as a BOS or CHoCH?",
        a: "No. Both BOS and CHoCH require a body close beyond the level. A wick through with the close back inside is a liquidity sweep, not a structural event. Mixing these up is the single most common reading error.",
      },
      {
        q: "What comes first, CHoCH or BOS?",
        a: "In a trend reversal, CHoCH comes first. CHoCH signals the prior trend is finished. The new trend is then confirmed by the first BOS in the new direction. Pyramiding entries before that confirmation is risky.",
      },
      {
        q: "Should I always trade a CHoCH?",
        a: "No. CHoCH against H4 bias has a far lower hit rate than CHoCH aligned with H4. The reliable setup is CHoCH that aligns with H4, immediately after a liquidity sweep, with entry on the retrace into the order block.",
      },
      {
        q: "What swing points should I use for structure?",
        a: "Use swings that have at least two candles on each side, ideally three. Internal noise creates false BOS and CHoCH signals. On XAU/USD, the H4 swings are the cleanest reference for bias.",
      },
    ],
    relatedSlugs: [
      "what-is-smart-money-concepts",
      "liquidity-sweep-gold-trading",
      "smc-trading-strategy-gold",
    ],
    Content: () => import("@/content/articles/choch-vs-bos-smart-money").then((m) => ({ default: m.ChochVsBosSmartMoneyContent })),
  },
  {
    slug: "breaker-block-trading-gold",
    title: "Breaker Block Trading on Gold: The Failed Order Block Playbook",
    seoTitle: "Breaker Block Trading on Gold",
    excerpt:
      "A Breaker Block is a failed Order Block that flips polarity. Learn how to identify, qualify, and trade breaker retests on XAU/USD with institutional precision.",
    category: "SMC Foundations",
    author: "XAU Pro Academy Team",
    publishedAt: "2026-10-05",
    updatedAt: "2026-10-05",
    readTime: "7 min",
    heroImage: breakerBlockCover.url,
    coverGradient: "from-gold-muted via-card to-emerald-muted",
    metaDescription:
      "Breaker Block trading on gold explained: how a failed Order Block flips into a high-probability entry zone on XAU/USD, with qualification rules, examples, and invalidation.",
    focusKeyword: "Breaker Block Trading",
    secondaryKeywords: [
      "Breaker Block SMC",
      "Breaker Block Gold",
      "XAUUSD Order Block",
      "Smart Money Concepts Entry",
    ],
    keyTakeaways: [
      "A Breaker Block is a failed Order Block whose polarity flips after a displacement break.",
      "Valid breakers on gold require a liquidity sweep, a body close through the block, displacement, and structure impact.",
      "The best entries combine the breaker retest with the Fair Value Gap left by the displacement leg.",
      "A body close back through the breaker is objective invalidation. Exit, never widen the stop.",
    ],
    toc: [
      { id: "introduction", label: "When Support Becomes Resistance" },
      { id: "what-is-breaker", label: "What Is a Breaker Block?" },
      { id: "anatomy", label: "Anatomy of a Valid Breaker" },
      { id: "xauusd-example", label: "A XAU/USD Example" },
      { id: "breaker-vs-ob", label: "Breaker vs Order Block" },
      { id: "risk", label: "Risk and Invalidation" },
      { id: "conclusion", label: "Conclusion" },
    ],
    faq: [
      {
        q: "What is a Breaker Block in trading?",
        a: "A Breaker Block is an Order Block that failed. Price breaks through it with displacement, and the broken zone flips role: a broken bearish block becomes demand on the retest, and a broken bullish block becomes supply. The flip is driven by trapped traders exiting at breakeven when price returns to their entry.",
      },
      {
        q: "How do you identify a valid Breaker Block on gold?",
        a: "Require four conditions: a liquidity sweep before the break, a candle body close through the block (not a wick), visible displacement ideally leaving a Fair Value Gap, and a Break of Structure or Market Structure Shift on the execution timeframe.",
      },
      {
        q: "What is the difference between an Order Block and a Breaker Block?",
        a: "An Order Block is a first-touch continuation zone that has not been violated. A Breaker Block is a second-chance reversal zone: the market already proved the original level wrong, and the polarity flip is the edge. Order Blocks suit trend continuation; breakers suit entries right after a structure shift.",
      },
      {
        q: "Where do you place a stop loss on a breaker trade?",
        a: "Beyond the liquidity sweep that originated the move, not just beyond the block itself. On XAU/USD, extremely tight stops do not survive normal volatility, so the stop should respect the sweep extreme and current ATR conditions.",
      },
      {
        q: "Do Breaker Blocks work on all timeframes?",
        a: "They appear on all timeframes, but quality scales with timeframe. On gold we qualify breakers on M15 and above, with H4 breakers carrying the most weight. Sub-M5 breakers are mostly noise and session spread.",
      },
    ],
    relatedSlugs: [
      "order-blocks-fvg-explained",
      "liquidity-sweep-gold-trading",
      "choch-vs-bos-smart-money",
      "high-probability-entry-criteria-gold",
    ],
    Content: () => import("@/content/articles/breaker-block-trading-gold").then((m) => ({ default: m.BreakerBlockGoldContent })),
  },
  {
    slug: "premium-discount-zones-gold",
    title: "Premium and Discount Zones: Buying Gold at the Right Price",
    seoTitle: "Premium and Discount Zones on Gold",
    excerpt:
      "Premium and Discount zones turn a vague pullback into a measurable rule. Learn how the 50% equilibrium and the Optimal Trade Entry band filter gold entries.",
    category: "SMC Foundations",
    author: "XAU Pro Academy Team",
    publishedAt: "2026-10-05",
    updatedAt: "2026-10-05",
    readTime: "6 min",
    heroImage: premiumDiscountCover.url,
    coverGradient: "from-gold-muted via-card to-emerald-muted",
    metaDescription:
      "Premium and Discount zones on XAU/USD: how the 50% equilibrium and the Optimal Trade Entry band (50% to 78.6%) filter gold entries and improve risk-adjusted results.",
    focusKeyword: "Premium and Discount Zones",
    secondaryKeywords: [
      "Optimal Trade Entry",
      "OTE Fibonacci Gold",
      "Equilibrium Trading Range",
      "SMC Dealing Range",
    ],
    keyTakeaways: [
      "Split any dealing range at the 50% equilibrium: above is premium, below is discount.",
      "Buy at a discount with a bullish bias, sell at a premium with a bearish bias. Location is a filter, not a parameter.",
      "The 50% to 78.6% band (Optimal Trade Entry) is where institutional re-accumulation most often occurs.",
      "Our multi-year gold research shows deep-discount entries materially outperform entries near equilibrium or in premium.",
    ],
    toc: [
      { id: "introduction", label: "The Price You Pay Matters" },
      { id: "definitions", label: "Defining Premium and Discount" },
      { id: "ote", label: "The Optimal Trade Entry Zone" },
      { id: "xauusd-application", label: "Applying It on XAU/USD" },
      { id: "common-mistakes", label: "Common Mistakes" },
      { id: "conclusion", label: "Conclusion" },
    ],
    faq: [
      {
        q: "What are Premium and Discount zones in trading?",
        a: "They are the two halves of a dealing range split at the 50% midpoint, called equilibrium. Everything above the midpoint is premium (expensive), everything below is discount (cheap). The institutional rule is to buy at a discount and sell at a premium, never the reverse.",
      },
      {
        q: "What is the Optimal Trade Entry (OTE)?",
        a: "The OTE is the deep retracement band between the 50% and 78.6% levels of a range. It is where institutional re-accumulation most often occurs before continuation, and it offers the best combination of entry price and stop distance.",
      },
      {
        q: "How do I choose the correct range to measure?",
        a: "Use the most recent significant higher-timeframe swing high and swing low that define the current leg. On gold we anchor to H4 swings. Measuring from random minor swings produces meaningless zones.",
      },
      {
        q: "Can I buy in premium if the setup is strong?",
        a: "You can, but the statistics argue against it. A bullish entry in premium compresses reward-to-risk and raises the chance of being caught in a normal pullback. The disciplined answer is usually to skip and wait for price to retrace into discount.",
      },
      {
        q: "Does the premium/discount rule work in bearish trends?",
        a: "Yes, mirrored. In a bearish leg you look to sell retracements into the premium half of the range, ideally where a POI overlaps the 50% to 78.6% band.",
      },
    ],
    relatedSlugs: [
      "high-probability-entry-criteria-gold",
      "order-blocks-fvg-explained",
      "smc-gold-trading-strategy",
      "breaker-block-trading-gold",
    ],
    Content: () => import("@/content/articles/premium-discount-zones-gold").then((m) => ({ default: m.PremiumDiscountZonesGoldContent })),
  },
  {
    slug: "gold-trading-sessions-london-new-york",
    title: "Gold Trading Sessions & Liquidity Windows: London, New York and Judas Swing",
    seoTitle: "Gold Trading Sessions and Liquidity Windows",
    excerpt:
      "Read XAUUSD session hours, daylight saving shifts, Asian range liquidity and conditional Judas Swing setups. Confirm structure and risk before trading London or New York.",
    category: "Market Structure",
    author: "XAU Pro Academy Team",
    publishedAt: "2026-10-05",
    updatedAt: "2026-10-08",
    readTime: "12 min",
    heroImage: goldSessionsCover.url,
    coverGradient: "from-gold-muted via-card to-emerald-muted",
    metaDescription:
      "Gold trading sessions and liquidity windows: UTC hours, DST shifts, Asian range, London Judas Swing confirmation, New York overlap and news-risk checklist.",
    focusKeyword: "Gold Trading Sessions",
    secondaryKeywords: [
      "London Open Gold Strategy",
      "New York Session XAUUSD",
      "Judas Swing Gold",
      "Best Time to Trade Gold",
      "XAUUSD session hours UTC",
      "Gold liquidity windows",
      "London New York overlap gold",
      "Gold kill zones daylight saving",
    ],
    keyTakeaways: [
      "Session hours are observation conventions, not a single spot-gold exchange schedule or an entry signal.",
      "Anchor London and New York windows to local time. Their daylight saving changes occur on different dates.",
      "A Judas Swing requires rejection and opposite displacement; a liquidity sweep alone does not confirm reversal.",
      "Use H4 and H1 context, then M15 confirmation and a qualified POI. Reject unclear structure and inadequate reward relative to risk.",
      "US releases, venue spreads and drawdown rules can veto a technically valid idea. No trade is an acceptable result.",
    ],
    toc: [
      { id: "introduction", label: "Timing Is Context, Not a Signal" },
      { id: "session-hours", label: "Session Hours and Liquidity Windows" },
      { id: "daylight-saving", label: "Daylight Saving and Kill Zones" },
      { id: "asian-session", label: "Mapping the Asian Session" },
      { id: "london-open", label: "Reading the London Open" },
      { id: "judas-swing", label: "Judas Swing Confirmation" },
      { id: "new-york", label: "New York and the Overlap" },
      { id: "news-risk", label: "CPI, NFP and News Risk" },
      { id: "practical-rules", label: "Checklist and Rejection Rules" },
      { id: "conclusion", label: "Conclusion" },
      { id: "sources", label: "Sources and Scope" },
      { id: "faq", label: "Frequently Asked Questions" },
    ],
    faq: [
      {
        q: "What is the best session to trade gold?",
        a: "London's opening window and the London and New York overlap are useful observation periods. Neither guarantees good execution or a profitable trade. Require confirmed structure, a qualified POI, acceptable costs and manageable news risk.",
      },
      {
        q: "What is the Judas Swing in gold trading?",
        a: "It is an SMC label for a false expansion near a session opening window followed by rejection and a move the other way. A sweep alone is insufficient: require opposite displacement and structural confirmation. The pattern does not prove manipulation or guarantee reversal.",
      },
      {
        q: "Should I trade gold during the Asian session?",
        a: "Asian hours can be balanced or directional. Mark a consistently defined overnight range when one exists, but do not assume every Asian breakout fails. Any entry still requires structure, liquidity interaction, displacement and acceptable venue conditions.",
      },
      {
        q: "When is the London and New York gold overlap in UTC?",
        a: "Using 08:00 New York to 17:00 London as the observation convention, it is 12:00 to 16:00 UTC in aligned summer and 13:00 to 17:00 UTC in aligned winter. During the US and UK daylight saving mismatch weeks it is 12:00 to 17:00 UTC. These are not official spot-gold exchange hours.",
      },
      {
        q: "How do US news releases affect session trading on gold?",
        a: "CPI and the Employment Situation report are scheduled at 08:30 Eastern Time, or 12:30 UTC in US daylight saving and 13:30 UTC in standard time. Verify the official calendar. FOMC uses a separate schedule. Spreads and fills can deteriorate around news; resume only when conditions and structure are interpretable.",
      },
      {
        q: "Why avoid trading gold late on Friday?",
        a: "Broker closure, changing spreads and weekend gaps can prevent fills at intended prices. Check the venue schedule and funded-account holding rules before taking or carrying exposure. A stop is not a guaranteed execution price.",
      },
    ],
    relatedSlugs: [
      "amd-pattern-gold-daily-cycles",
      "liquidity-sweep-gold-trading",
      "choch-vs-bos-smart-money",
      "high-probability-entry-criteria-gold",
    ],
    Content: () => import("@/content/articles/gold-trading-sessions-london-new-york").then((m) => ({ default: m.GoldTradingSessionsContent })),
  },
  {
    slug: "gold-slippage-spread-expansion-blackouts",
    title: "Gold Slippage, Spread Expansion & Liquidity Blackouts: The Execution Manual",
    seoTitle: "Gold Slippage and Spread Expansion",
    excerpt:
      "Why XAUUSD stops fill beyond their price during CPI, NFP, the daily break and weekend gaps, and how slippage breaches funded-account limits. Includes an interactive simulator.",
    category: "Risk Management",
    author: "XAU Pro Academy Team",
    publishedAt: "2026-10-08",
    updatedAt: "2026-10-08",
    readTime: "11 min",
    heroImage: goldSlippageCover.url,
    coverGradient: "from-gold-muted via-card to-emerald-muted",
    metaDescription:
      "Gold slippage and spread expansion explained: stop fills during CPI, NFP, rollover and weekend gaps, prop firm drawdown risk and an interactive slippage simulator.",
    focusKeyword: "Gold Slippage",
    secondaryKeywords: [
      "XAUUSD spread expansion",
      "Gold liquidity blackout",
      "Prop firm gold slippage",
      "CPI NFP gold spread",
      "Gold weekend gap",
      "Stop loss slippage gold",
      "Gold rollover spread",
    ],
    keyTakeaways: [
      "A stop is a trigger, not a guaranteed price. Once touched it fills at the best available price.",
      "Spread is known before entry; slippage is known only after the fill and depends on depth.",
      "The daily dealing break, scheduled US data and the weekend gap are the recurring blackout windows.",
      "Each $1.00 per ounce of slippage costs $100 per standard lot. Budget it inside your daily limit.",
      "Funded-account rules see the fill, not the plan. Decide on exposure before the event, not during it.",
    ],
    toc: [
      { id: "introduction", label: "Your Stop Is a Trigger" },
      { id: "mechanics", label: "Spread, Slippage and Depth" },
      { id: "blackout-windows", label: "The Three Liquidity Blackouts" },
      { id: "simulator", label: "Interactive Slippage Simulator" },
      { id: "prop-firms", label: "Funded Accounts and Fills" },
      { id: "protocol", label: "Institutional Execution Protocol" },
      { id: "conclusion", label: "Conclusion" },
      { id: "sources", label: "Sources and Scope" },
      { id: "faq", label: "Frequently Asked Questions" },
    ],
    faq: [
      {
        q: "What causes slippage on gold?",
        a: "Slippage occurs when the price you receive differs from the price you requested or triggered. On XAUUSD it grows when liquidity providers widen or withdraw quotes, typically around US data releases, the daily dealing break and the weekend reopening.",
      },
      {
        q: "Does a stop-loss guarantee my maximum loss?",
        a: "No. A standard stop becomes a market order when triggered and fills at the next available price. In thin conditions or gaps that price can be well beyond the stop level. Only guaranteed stops, where offered, cap the fill, usually at an extra cost.",
      },
      {
        q: "When are gold spreads widest?",
        a: "Commonly around the daily break near 17:00 New York time, in the seconds around high-impact US releases such as CPI, NFP and FOMC, and at the Sunday reopening. Exact behaviour varies by venue, so measure your own broker.",
      },
      {
        q: "How much does $1 of slippage cost on one lot of gold?",
        a: "One standard XAUUSD lot is 100 troy ounces, so $1.00 per ounce of slippage costs $100 per lot. Confirm the contract size with your broker.",
      },
      {
        q: "Can slippage breach a prop firm daily drawdown rule?",
        a: "Yes. Drawdown rules measure realised and often floating equity, not where your stop was placed. If planned risk uses most of the remaining daily allowance, a poor fill can push the realised loss past the limit.",
      },
    ],
    relatedSlugs: [
      "gold-trading-sessions-london-new-york",
      "smc-mistakes-prop-firm-challenge",
      "drawdown-psychology-funded-accounts",
      "funded-mindset-risk-management",
    ],
    Content: () => import("@/content/articles/gold-slippage-spread-expansion-blackouts").then((m) => ({ default: m.GoldSlippageContent })),
  },
  {
    slug: "fvg-vs-liquidity-void-gold",
    title: "FVG vs Liquidity Void in XAUUSD: Institutional Invalidation Rules",
    seoTitle: "FVG vs Liquidity Void in XAUUSD",
    excerpt:
      "How to separate a fair value gap from a liquidity void on gold, when a gap is invalidated, and how CE and inversion work. Includes an interactive invalidation calculator.",
    category: "SMC Foundations",
    author: "XAU Pro Academy Team",
    publishedAt: "2026-10-08",
    updatedAt: "2026-10-08",
    readTime: "10 min",
    heroImage: fvgVoidCover.url,
    coverGradient: "from-gold-muted via-card to-slate-900",
    metaDescription:
      "FVG vs liquidity void on XAUUSD: structural differences, body close invalidation, 50% consequent encroachment, inversion FVG and an interactive gold FVG calculator.",
    focusKeyword: "FVG vs Liquidity Void",
    secondaryKeywords: [
      "FVG invalidation",
      "Consequent encroachment gold",
      "Inversion FVG",
      "XAUUSD fair value gap rules",
      "Liquidity void trading",
    ],
    keyTakeaways: [
      "A fair value gap is a compact three candle imbalance; a liquidity void is a wide multi candle range with thin orders.",
      "Only a candle body close beyond the far boundary invalidates a gap. A wick is liquidity interaction.",
      "Strong gaps react in their upper half. A close beyond 50% CE downgrades the zone.",
      "An invalidated gap may act as an inversion zone, but only after displacement and structure confirm it.",
    ],
    toc: [
      { id: "introduction", label: "Not Every Gap Is an Entry" },
      { id: "definitions", label: "FVG and Liquidity Void" },
      { id: "invalidation-rules", label: "Institutional Invalidation Rules" },
      { id: "calculator", label: "Interactive Invalidation Calculator" },
      { id: "session-context", label: "Session and News Context" },
      { id: "checklist", label: "Pre-Execution Checklist" },
      { id: "conclusion", label: "Conclusion" },
      { id: "faq", label: "Frequently Asked Questions" },
    ],
    faq: [
      {
        q: "What is the difference between an FVG and a liquidity void?",
        a: "A fair value gap is a three candle imbalance created by one displacement candle with exact boundaries. A liquidity void is a wider range built from several one sided candles where resting orders were thin, so price often moves back through it quickly.",
      },
      {
        q: "When is a fair value gap invalidated?",
        a: "When a candle body closes beyond the far boundary of the gap. A wick through the boundary is treated as liquidity interaction, not invalidation.",
      },
      {
        q: "What is consequent encroachment?",
        a: "Consequent encroachment is the 50% level of a gap. Strong gaps usually react before or at this level. A body close beyond it weakens the zone without fully invalidating it.",
      },
      {
        q: "What is an inversion FVG?",
        a: "An inversion FVG is a gap that has been invalidated by a body close and is later retested from the other side, where it may act in the opposite direction. It needs displacement and a structure shift to be considered.",
      },
      {
        q: "Should I trade a gap formed during CPI or NFP?",
        a: "Gaps created by news repricing are often refilled quickly and spreads can be wide at the time. Treat them as context or targets and wait for normal liquidity before considering execution.",
      },
    ],
    relatedSlugs: [
      "fair-value-gap-xauusd-guide",
      "gold-slippage-spread-expansion-blackouts",
      "gold-trading-sessions-london-new-york",
      "choch-vs-bos-smart-money",
    ],
    Content: () => import("@/content/articles/fvg-vs-liquidity-void-gold").then((m) => ({ default: m.FvgVsLiquidityVoidContent })),
  },
];

// ---------- Visibility contract ----------
// Single source of truth that decides whether an article is public, a preview,
// or strictly an Academy lesson. Adding a new article? Add it here too.
const VISIBILITY: Record<string, ArticleVisibility> = {
  // Featured macro research (hero of /education)
  "gold-correction-5600-peak-macro-analysis": "public",
  "us-cpi-september-2026-gold-reaction": "public",
  // Top-of-funnel public articles
  "roadmap-2026-gold-trading": "public",
  "smc-mistakes-prop-firm-challenge": "public",
  "drawdown-psychology-funded-accounts": "public",
  // Formerly previews, now fully public for search indexing
  "smc-gold-trading-strategy": "public",
  "smc-vs-indicators-gold-trading": "public",
  "amd-pattern-gold-daily-cycles": "public",
  "liquidity-purge-eqh-eql-gold": "public",
  "inducement-trap-idm-smc": "public",
  "high-probability-entry-criteria-gold": "public",
  "funded-mindset-risk-management": "public",
  "smc-execution-checklist-prop-firm": "public",
  "drawdown-recovery-strategy": "public",
  // SMC pillar supporting articles (top-of-funnel public)
  "what-is-smart-money-concepts": "public",
  "order-blocks-fvg-explained": "public",
  "ict-vs-smc-explained": "public",
  // SEO content cluster targeting competitor-ranked keywords
  "fair-value-gap-xauusd-guide": "public",
  "liquidity-sweep-gold-trading": "public",
  "smc-trading-strategy-gold": "public",
  "ict-displacement-explained": "public",
  "choch-vs-bos-smart-money": "public",
  "gold-slippage-spread-expansion-blackouts": "public",
  "fvg-vs-liquidity-void-gold": "public",
  "institutional-desk-routine-gold": "public",
  "gold-traders-blind-spot-macrolens": "public",
  "gold-swing-trading-strategy-case-study": "public",
};

export function getArticleVisibility(slug: string): ArticleVisibility {
  return VISIBILITY[slug] ?? "public";
}

export function getArticleBySlug(slug: string): Article | undefined {
  const article = articles.find((a) => a.slug === slug);
  if (!article) return undefined;
  return { ...article, visibility: getArticleVisibility(slug) };
}

/** Articles that are surfaced on the public /education feed (public + preview). */
export function getPublicArticles(): Article[] {
  return articles
    .filter((a) => getArticleVisibility(a.slug) !== "academy")
    .map((a) => ({ ...a, visibility: getArticleVisibility(a.slug) }));
}

export function getNextArticle(slug: string): Article | undefined {
  const list = getPublicArticles();
  const idx = list.findIndex((a) => a.slug === slug);
  if (idx === -1) return list[0];
  return list[(idx + 1) % list.length];
}

const FEATURED_HOME_SLUG = "smc-gold-trading-strategy";
const HOME_PICK_SLUGS = ["fair-value-gap-xauusd-guide", "smc-trading-strategy-gold"];

export function getFeaturedHomeArticles(): Article[] {
  const featured = getArticleBySlug(FEATURED_HOME_SLUG);
  const picks = HOME_PICK_SLUGS
    .map((s) => getArticleBySlug(s))
    .filter((a): a is Article => Boolean(a) && getArticleVisibility(a!.slug) !== "academy");
  return featured ? [featured, ...picks] : picks;
}

export function getRelatedArticles(slug: string, limit = 3): Article[] {
  const current = getArticleBySlug(slug);
  if (!current) return [];
  const isPublic = (s: string) => getArticleVisibility(s) !== "academy";
  const explicit = current.relatedSlugs
    .filter(isPublic)
    .map((s) => getArticleBySlug(s))
    .filter((a): a is Article => Boolean(a));
  if (explicit.length >= limit) return explicit.slice(0, limit);
  const fillers = getPublicArticles().filter(
    (a) => a.slug !== slug && !current.relatedSlugs.includes(a.slug),
  );
  return [...explicit, ...fillers].slice(0, limit);
}
